// restaurant menu listing

import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';
import {
  get,
  isEqual,
  reduce,
  isEmpty,
  findIndex,
  values,
  filter,
  sortBy,
  join,
  cloneDeep,
  orderBy,
  includes,
  startCase,
  toLower,
  differenceBy,
  forEach,
  uniq,
  isUndefined,
} from 'lodash';
import produce from 'immer';
import scrollMonitor from 'scrollmonitor';
import { Divider } from '@mui/material';

import asyncRoute from '../../helpers/hoc/asyncRoute';
import {
  dineInModes,
  renderImageType,
  getTableLabel,
  scrollElementIntoView,
  getColorIndex,
  isUserLoggedIn,
  extractWhitelabelSubdomain,
  detectWebAppBrowser,
  isElementInViewPort,
  generateUUID,
  toTitleCase,
  isIOS,
  isInStandaloneMode,
  areSameArraysSansOrder,
  fetchGeoLocation,
  checkDistanceBetweenCoordinates,
  isNativeWebView,
} from '../../utils/helpers';
import {
  fudrAnalyticsGTMEventCategory,
  fudrAnalyticsGTMEventLabel,
  fudrAnalyticsGTMEvents,
} from '../../utils/FudrAnalytics/config';
import {
  apiResponseCodes,
  authInitiatorFeatures,
  bannerStyles,
  browserList,
  cashCardScreens,
  cashCardUnitPointMonetaryFactor,
  devicesRenderingMenuPWAWidgetsList,
  featureSpecificAPIErrorCodes,
  restaurantCashCardInjectablePositionOnMenu,
  serverErrorCodes,
  toastMessageTime,
  upsellSearchErrorCase,
  currencies,
  repeatOrdersCount,
  permissionStatus,
  analyticsVendorType,
  storageTypes,
  geoLocationConfig,
} from '../../constants/globals';
import { constants } from '../../constants/configuration';
import {
  getTotalItemCount,
  selectCart,
  selectMenu,
  selectRestaurant,
  getErrorTitle,
  getErrorDescription,
  getButtonHandlerCta,
  getRestaurantOperatingMode,
  renderRestaurantOperatingModeMessage,
  getTrueCallerDeepLink,
  getTrueCallerErrorContent,
  getUpsellErrorPopupContent,
  shouldOfferBeVisibleOnMenu,
  getRestaurantAdditionalPropertyValue,
} from '../../selectors';
import routes from '../../routes';
import { DeviceContext } from '../../containers';
import { getItem, setItem } from '../../utils/storage';

import {
  fetchRestaurantMenuAction,
  fetchRestaurantOffersAction,
  fetchMenuItemCustomizationsAction,
  clearPreviousCustomization,
  fetchViewCartItemsAction,
  updateMenuAction,
  clearViewCartItems,
  clearApiErrorResponseAction,
  clearDisabledMenuItems,
  updateAssistanceStatusAction,
  dispatchPreviousSelectedTableAction,
  fetchSelectedTableAction,
  updateProfileAction,
  dispatchSelectedAddressAction,
  dispatchParentRouteAction,
  fetchRestaurantCashCardAction,
  fetchGuestCashCardsAction,
  beginAjaxCallAction,
  endAjaxCallAction,
  dispatchLastCashCardRechargeTransactionAction,
  fetchGuestCashCardDetailsAction,
  clearRestaurantCashCardAction,
  dispatchUserCartAction,
  clearProgressiveWebAppDataAction,
  fetchGuestPreviousOrdersAction,
  clearGuestPreviousOrdersAction,
  findGuestLocationAction,
  findGeoLocationAction,
  dispatchProgressiveWebAppDataAction,
  fetchMenuItemVariationsAction,
} from '../../actions/app.action';

import {
  signInByTrueCallerAction,
  dispatchGuestLoginDataAction,
} from '../../actions/authentication';

import { fetchAppStartupInfoAction } from '../../actions/common';
import rewardsRoutes from '../../routes/rewards';

import menuBtnImage from '../../assets/images/icons/menu-button.svg';
import pointCoinSymbol from '../../assets/images/icons/point-coin.svg';
import forwardArrowIcon from '../../assets/images/icons/arrow-forward-white.svg';
import emptyMenuPlaceholder from '../../assets/images/empty-menu-placeholder.svg';

import './Restaurant.scss';

const ImageHybrid = asyncRoute(() => import('../../components/ImageHybrid'));
const Header = asyncRoute(() => import('../../components/Header'));
const ScanQR = asyncRoute(() => import('../../components/ScanQR'));
const MenuModal = asyncRoute(() => import('../../components/MenuModal'));
const Drawer = asyncRoute(() => import('../../components/Drawer'));
const FoodItem = asyncRoute(() => import('../../components/FoodItem'));
const CartFooter = asyncRoute(() => import('../../components/cartFooter'));
const CustomizationMenu = asyncRoute(() => import('../../components/Popup/CustomizationMenu'));
const WatchComponent = asyncRoute(() => import('../../components/watchComponent'));
const AuthenticationPopup = asyncRoute(() => import('../../components/Popup/AuthenticationPopup'));
const ExceptionComponent = asyncRoute(() => import('../../components/ExceptionComponent'));
const MultipleCustomizationPopup = asyncRoute(() =>
  import('../../components/Popup/MultipleCustomizationPopup'),
);
const Services = asyncRoute(() => import('../../components/Popup/Services'));
const Description = asyncRoute(() => import('../../components/elements/Description'));
const SelfPickupComponent = asyncRoute(() => import('../../components/Popup/SelfPickupPopup'));
const DeliveryCard = asyncRoute(() => import('../../components/Card/DeliveryCard'));
const DeliveryAddressPopup = asyncRoute(() => import('../../components/Popup/DeliveryAddress'));
const VerifyCartPopup = asyncRoute(() => import('../../components/Popup/VerifyCart'));
const OrderModePopup = asyncRoute(() => import('../../components/Popup/OrderMode'));
const MenuItemDetailPopup = asyncRoute(() => import('../../components/Popup/MenuItemDetail'));
const OfferCard = asyncRoute(() => import('../../components/Card/OfferCard'));
const PreviousOrderCard = asyncRoute(() => import('../../components/Card/PreviousOrderCard'));
const OfferDetailsPopup = asyncRoute(() => import('../../components/Popup/OfferDetailsPopup'));
const Footer = asyncRoute(() => import('../../components/Footer'));
const Banner = asyncRoute(() => import('../../components/elements/Banner'));
const ExclusiveOfferCard = asyncRoute(() => import('../../components/Card/ExclusiveOfferCard'));
const InstallAppetizerCard = asyncRoute(() => import('../../components/Card/InstallAppetizer'));
const InstallAppetizerPopup = asyncRoute(() => import('../../components/Popup/InstallAppetizer'));
const SafariPWAInstallGuide = asyncRoute(() =>
  import('../../components/Popup/InstallAppetizer/SafariGuide'),
);
const Toast = asyncRoute(() => import('../../components/elements/Toast'));
const CashCardPreview = asyncRoute(() => import('../../components/Card/CashCardPreview'));
const CashCardInfoPopup = asyncRoute(() => import('../../components/Popup/CashCard/Info'));
const RechargeScreen = asyncRoute(() => import('../../components/RechargeScreen'));
const AvailedUpsellItemCard = asyncRoute(() => import('../../components/Card/AvailedUpsellItem'));
const Popup = asyncRoute(() => import('../../components/Popup'));
const PreviousOrderDetailsPopup = asyncRoute(() =>
  import('../../components/Popup/PreviousOrderDetailsPopup'),
);
const Skeleton = asyncRoute(() => import('../../components/Skeleton'));
const PermissionEvent = asyncRoute(() => import('../../utils/FudrAnalytics/permissionEvent'));
const InstallAppCard = asyncRoute(() => import('../../components/Card/InstallApp'));
const InstallAppPopup = asyncRoute(() => import('../../components/Popup/InstallApp'));

const menuScreenConfig = constants.menu;
const authenticationConfig = constants.authentication;
const cartScreenConfig = constants.cart;
class Restaurant extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showScanQRComponent: false,
      visible: false,
      isCustomizable: false,
      customizeItem: {},
      activeItem: '',
      categories: [],
      countMap: {},
      count: 0,
      scrollContainer: {},
      items: [],
      selectedCustomization: [],
      selectedCustomizationArr: [],
      selectedVariationArr: [],
      openRepeatCustomizationPopup: false,
      currentItem: {},
      addNewItemFlag: false,
      showAuthenticationPopup: false,
      showMultipleCustomizationPopup: false,
      loginData: {},
      cart: {},
      showModal: false,
      includeMenu: true,
      searchValue: '',
      showEmptySearchMessage: false,
      showErrorPopup: false,
      showServicesComponent: false,
      showSelfPickupPopupComponent: false,
      showDeliveryAddressPopup: false,
      showVerifyCartPopup: false,
      showOrderModePopup: false,
      errorContent: {
        title: '',
        description: '',
        buttonCta: '',
      },
      page: routes.containers.restaurant,
      showSelfPickUpSection: false,
      showMenuItemDetailPopup: false,
      mediaItems: [],
      showOfferDetailsPopup: false,
      isMenuEmpty: false,
      offer: {},
      banner: {
        position: 'bottom',
        color: bannerStyles.bgColors.red,
      },
      showPWAInstallPopup: false,
      showRestaurantCashCardInfoPopup: false,
      noOffersToast: '',
      loginInitiatorContext: '', // Stores the feature's name that initiated the login
      hasGuestIssuedRestaurantCashCard: false,
      showRechargeConfirmationScreen: false,
      showRechargeOngoingScreen: false,
      showPopup: false,
      popupContent: '',
      popupChildren: '',
      showCustomizationErrorCTA: false,
      previousOrdersAddedToCart: [], // Ids of the previous orders from which menu items have been added to cart
      selectedPreviousOrder: {}, // Stores the target previous order's data for the order details popup context
      loadingPreviousOrder: false,
      geoLocationPermissionStatus: '',
      trackAnalyticEvent: false,
      showInstallAppPopup: false,
    };

    this.scrollRef = React.createRef();
    this.tabRefs = [];
    this.handleSwitchRestaurantClick = this.handleSwitchRestaurantClick.bind(this);
    this.handleViewOfferDetailsClick = this.handleViewOfferDetailsClick.bind(this);
    this.onCloseOfferDetailsPopup = this.onCloseOfferDetailsPopup.bind(this);
    this.handleItemClick = this.handleItemClick.bind(this);
    this.closeMenuModal = this.closeMenuModal.bind(this);
  }

  componentDidMount() {
    const { match, loginData, cashCardFeatureEnabled, restaurant } = this.props;
    const { includeMenu } = this.state;
    const restaurantIdAndTableId = get(match, 'params.restaurantAndTableId', '');
    const guestId = get(loginData, 'id', '');

    // get location on App load
    if (!isNativeWebView) {
      fetchGeoLocation(this.geoLocationCallback);
      //  check geolocation permission
      this.checkGeoLocationPermission();
    }
    this.props.actions.dispatchParentRouteAction(get(match, 'url', ''));
    this.props.actions.updateAssistanceStatusAction();

    // get restaurant menu data with includeMenu === true
    this.props.actions
      .fetchRestaurantMenuAction(restaurantIdAndTableId, includeMenu)
      .then((response) => {
        const isMenuOpenByDefault =
          getRestaurantAdditionalPropertyValue(restaurant, 'isNavigationOpenOnLoad') === 'true';

        //set if menu empty or not
        if (!isUndefined(get(response, 'sections'))) {
          const isMenuEmpty = this.checkIsMenuEmpty(get(response, 'sections'));
          this.setState({
            isMenuEmpty,
            showModal: !isMenuEmpty && isMenuOpenByDefault,
          });
        }

        const serviceType = get(response, 'selectedTable.type', '');
        const restaurantId = get(response, 'restaurant.id', '');
        let restaurantOffersReqBody = {
          restaurantId,
          serviceType,
          guestId,
        };
        //fetch restaurant offers
        this.props.actions.fetchRestaurantOffersAction({ restaurantOffersReqBody });
        // Fetch whitelabel app related data
        let whitelabelSubDomain = get(restaurant, 'subDomain', '');
        if (isEmpty(whitelabelSubDomain)) {
          whitelabelSubDomain = get(restaurant, 'engageBrand', '');
        }
        const appStartupReqBody = {
          subdomain: whitelabelSubDomain,
          includeModules: ['whiteLabelApp', 'reward', 'brand', 'resource'],
        };
        whitelabelSubDomain &&
          this.props.actions.fetchAppStartupInfo({ appStartupReqBody }).then((response) => {
            if (apiResponseCodes.SUCCESS.includes(get(response, 'status', ''))) {
              const progressiveWebAppData = get(response, 'data.whiteLabelApp', {});
              this.props.actions.dispatchProgressiveWebAppData({ progressiveWebAppData });
            }
          });

        if (cashCardFeatureEnabled) {
          const { lastCashCardRechargeTransaction } = this.props;
          !isEmpty(lastCashCardRechargeTransaction) &&
            this.viewCashCardRechargeConfirmationDetails();
          // If cash card feature is enabled, fetch cash card data contextual to the guest or the outlet based on whether the guest is logged-in or not
          this.populateCashCardDataByGuestContext(restaurantId, guestId);
        }
        //get guest previous order on the restaurant
        if (!isEmpty(guestId)) {
          const requestBody = {
            restaurantId,
            guestId,
            size: repeatOrdersCount,
          };
          this.setState({ loadingPreviousOrder: true }, () => {
            this.props.actions
              .fetchGuestPreviousOrder(requestBody)
              .then(() => this.setState({ loadingPreviousOrder: false }));
          });
        }
      });

    // Repopulate items state from the session's cart
    const sessionCartItems = get(this.props.cart, 'items', []);
    !isEmpty(sessionCartItems) &&
      this.setState({ items: sessionCartItems }, () => {
        // for countMap already set for existing cart
        this.calculateCountCost(this.state.countMap, this.state.items);
      });

    // scroll container watcher
    this.setState({
      scrollContainer: scrollMonitor.createContainer(this.scrollRef.current),
    });
  }

  geoLocationCallback = (position) => {
    if (!!position) {
      let location = {};
      const guestGeolocation = getItem(storageTypes.localStorage, 'geoLocation');
      const guestLocation = getItem(storageTypes.localStorage, 'guestLocation');
      const guestLoginData = JSON.parse(localStorage.getItem('GuestLoginData'));
      const guestId = get(guestLoginData, 'id', '');
      const timeStamp = get(guestLocation, 'timeStamp', '');
      const differenceInTimeStamps = (Date.now() - timeStamp) / 60000; // divided by 60000 to convert it into minutes
      const latitude1 = get(
        guestGeolocation,
        'latitude',
        get(guestLocation, 'city.defaultLatitude'),
      );
      const longitude1 = get(
        guestGeolocation,
        'longitude',
        get(guestLocation, 'city.defaultLongitude'),
      );
      const latitude2 = position.coords && position.coords.latitude; // current coordiantes from geoLocationAPI
      const longitude2 = position.coords && position.coords.longitude; // current coordiantes from geoLocationAPI
      // set the current detected coordinates into location object and then into localStorage
      location['latitude'] = latitude2;
      location['longitude'] = longitude2;
      setItem(storageTypes.localStorage, 'geoLocation', location);

      const distance = checkDistanceBetweenCoordinates({
        latitude1,
        longitude1,
        latitude2,
        longitude2,
      });
      if (
        isEmpty(guestLocation) ||
        differenceInTimeStamps > geoLocationConfig.maxDifferenceBetweenTimeStamps ||
        distance > geoLocationConfig.maxDistanceInKm
      ) {
        this.props.actions.findGuestLocationAction({ guestId }); // find guest location if geo locatin coordinates are present
      }
    } else {
      this.props.actions.findGeoLocationAction(); // find coordinates through google geolocation api
    }
  };

  checkGeoLocationPermission = () => {
    navigator.geolocation.getCurrentPosition(
      () =>
        this.setState({
          geoLocationPermissionStatus: permissionStatus.allow,
          trackAnalyticEvent: true,
        }),
      () =>
        this.setState({
          geoLocationPermissionStatus: permissionStatus.block,
          trackAnalyticEvent: true,
        }),
    );
  };

  checkIsMenuEmpty = (menu) => {
    if (isEmpty(menu)) {
      return true;
    } else {
      let isMenuEmpty = true;
      menu.forEach((section) => {
        if (section.isEnabled) {
          if (
            !isEmpty(section.menuItems) &&
            section.menuItems.some((menuItem) => menuItem.isEnabled)
          ) {
            isMenuEmpty = false;
            return;
          }
          if (
            !isEmpty(section.categories) &&
            section.categories.some((category) => category.isEnabled)
          ) {
            section.categories.forEach((category) => {
              if (category.isEnabled) {
                if (
                  !isEmpty(category.menuItems) &&
                  category.menuItems.some((menuItem) => menuItem.isEnabled)
                ) {
                  isMenuEmpty = false;
                  return;
                }
              }
            });
          }
        }
      });
      return isMenuEmpty;
    }
  };

  componentDidUpdate(prevProps, prevState) {
    const { countMap, items, toast, noOffersToast } = this.state;
    const { isAssistanceUpdated, loginData, restaurant } = this.props;

    if (!isEqual(get(this.props, 'menu'), get(prevProps, 'menu'))) {
      this.setState({ isMenuEmpty: this.checkIsMenuEmpty(get(this.props, 'menu')) });
    }

    if (
      !isEmpty(prevProps.viewCartItems) &&
      !isEqual(this.props.viewCartItems, prevProps.viewCartItems)
    ) {
      this.setCartState(prevProps.viewCartItems);
    }
    if (!isEmpty(countMap) && !isEqual(prevState.countMap, countMap)) {
      this.calculateCountCost(countMap, items);
    }
    if (isAssistanceUpdated) {
      const assistanceElement = document.getElementById('assistance-success');
      if (assistanceElement) {
        setTimeout(function () {
          document.getElementById('assistance-success').style.visibility = 'hidden';
        }, toastMessageTime);
      }
    }

    // Fetch restaurant offers when a guest logs in to retrieve guest-specific offers
    if (!isEmpty(this.props.loginData) && !isEqual(this.props.loginData, prevProps.loginData)) {
      const { restaurant, loginData, selectedTable, cashCardFeatureEnabled } = this.props;
      const restaurantId = get(restaurant, 'id', '');
      const guestId = get(loginData, 'id', '');
      const serviceType = get(selectedTable, 'type', '');
      const restaurantOffersReqBody = {
        restaurantId,
        serviceType,
        guestId,
      };
      !isEmpty(restaurantId) &&
        !isEmpty(serviceType) &&
        this.props.actions
          .fetchRestaurantOffersAction({ restaurantOffersReqBody })
          .then((restaurantOffersResponse) => {
            if (apiResponseCodes.SUCCESS.includes(get(restaurantOffersResponse, 'status', ''))) {
              const restaurantOffers = get(restaurantOffersResponse, 'data.offers', []);
              const restaurantActiveOffers = restaurantOffers.filter(
                (offer) => !!get(offer, 'isActive', false),
              );
              // Show 'no offers' toast in case no restaurant offers are available
              if (isEmpty(restaurantActiveOffers)) {
                const noOffersToastDelay = !!toast ? toastMessageTime + 200 : 0; // Add a delay (with a 200 deliberation gap) to the no offers toast display in case a toast is already being shown
                setTimeout(() => {
                  this.setState({
                    noOffersToast: constants.offers.exclusiveGuestOffers.noOffersToastMessage,
                  });
                }, noOffersToastDelay);
              }
            }
          });

      // On login midway while browsing the Menu screen, fetch the guest cash card data issued on the outlet
      // If cash card feature is enabled
      if (cashCardFeatureEnabled && !!guestId) {
        const constructedURL = `/cashCards/guests?guestId=${guestId}&restaurantIds=${restaurantId}`;

        this.props.actions.fetchGuestCashCards({ url: constructedURL }).then((response) => {
          // On API call success, if the guest cash card for visited outlet is present,
          // set hasGuestIssuedRestaurantCashCard state to true; otherwise bring outlet cash card details
          if (apiResponseCodes.SUCCESS.includes(get(response, 'status'))) {
            const { guestCashCards } = this.props;

            if (!isEmpty(guestCashCards)) {
              this.setHasGuestIssuedRestaurantCashCard(true);
              const [guestCashCardObj] = guestCashCards;
              const guestCashCardId = get(guestCashCardObj, 'id', '');
              this.props.actions.fetchGuestCashCardDetails({ guestCashCardId });
            }
          }
        });
      }

      // If cash card info popup initiated the login flow, open it again
      this.state.loginInitiatorContext === authInitiatorFeatures.cashCardRecharge &&
        this.setShowRestaurantCashCardInfoPopup(true);
    }

    // Hide 'no offers' toast after configured delay
    if (!isEmpty(noOffersToast) && !isEqual(noOffersToast, prevState.noOffersToast)) {
      setTimeout(
        () =>
          this.setState({
            noOffersToast: '',
          }),
        toastMessageTime,
      );
    }

    //get guest previous order on the restaurant
    if (
      !isEmpty(get(loginData, 'id')) &&
      !isEmpty(restaurant, 'id') &&
      (!isEqual(get(loginData, 'id'), get(prevProps, 'loginData.id')) ||
        !isEqual(get(restaurant, 'id'), get(prevProps, 'restaurant.id')))
    ) {
      const requestBody = {
        restaurantId: get(restaurant, 'id'),
        guestId: get(loginData, 'id'),
        size: repeatOrdersCount,
      };
      this.setState({ loadingPreviousOrder: true }, () => {
        this.props.actions
          .fetchGuestPreviousOrder(requestBody)
          .then(() => this.setState({ loadingPreviousOrder: false }));
      });
    }

    // Generic toast state
    if (!isEmpty(toast) && !isEqual(toast, prevState.toast)) {
      setTimeout(
        () =>
          this.setState({
            toast: '',
          }),
        toastMessageTime,
      );
    }
  }

  viewCashCardRechargeConfirmationDetails = () => {
    this.setShowRechargeConfirmationScreen(true);
  };

  // Fetch and store cash card data contextual to the guest or the outlet based on whether the guest is logged-in or not
  populateCashCardDataByGuestContext = (restaurantId, guestId) => {
    if (!!guestId) {
      const constructedURL = `/cashCards/guests?guestId=${guestId}&restaurantIds=${restaurantId}`;

      this.props.actions.fetchGuestCashCards({ url: constructedURL }).then((response) => {
        // On API call success, if the guest cash card for visited outlet is present,
        // set hasGuestIssuedRestaurantCashCard state to true; otherwise bring outlet cash card details
        if (apiResponseCodes.SUCCESS.includes(get(response, 'status'))) {
          const { guestCashCards } = this.props;

          if (!isEmpty(guestCashCards)) {
            this.setHasGuestIssuedRestaurantCashCard(true);
          } else {
            // In case the guest is logged-in but has not issued any cash card from the outlet, bring the outlet's cash card data
            this.props.actions.fetchRestaurantCashCard({ restaurantId });
          }
        }
      });
    } else {
      // Simply bring the outlet's cash card data if the guest is not logged-in
      this.props.actions.fetchRestaurantCashCard({ restaurantId });
    }
  };

  handleMenuClick = (visible) => {
    this.setState({
      visible: !visible,
    });
  };

  onHide = () => {
    const { visible } = this.state;
    if (visible) {
      this.setState({
        visible: false,
      });
    }
  };

  onCustomizableHide = () => {
    this.setState(
      {
        isCustomizable: false,
        showMenuItemDetailPopup: false,
        showCustomizationErrorCTA: false,
      },
      () => {
        this.props.actions.clearPreviousCustomization();
      },
    );
  };

  // scroll to said item
  handleItemClick = (name, skipScroll = false, skipClosePopup = false) => {
    if (!skipScroll) {
      // https://github.com/iamdustan/smoothscroll/issues/47#issuecomment-350810238
      const item = this.tabRefs[name].current; // what we want to scroll to
      var count = !!item ? item.offsetTop - 160 : 0; // xx = any extra distance from top ex. 60

      window.scrollTo({
        top: count,
        left: 0,
        behavior: 'smooth',
      });
    }

    this.setState(
      {
        activeItem: name,
      },
      () => {
        !skipClosePopup && this.closeMenuModal();
      },
    );
  };

  openMenuModal = () => {
    const activeItem = isElementInViewPort({ elements: this.tabRefs, headerHeight: 160 });
    this.setState({
      showModal: true,
      activeItem,
    });
  };

  closeMenuModal = () => {
    this.setState({
      showModal: false,
    });
  };

  onAddItem = ({ id, price, hasVariations }, selectedCustomization, selectedVariation) => {
    const {
      countMap,
      items,
      addNewItemFlag,
      customizeItem,
      selectedCustomizationArr,
      selectedVariationArr,
    } = this.state;
    let countMapState = cloneDeep(countMap, true);
    let currentCompareKey = id;
    let index;
    if (addNewItemFlag || !isEmpty(selectedCustomization) || !isEmpty(selectedVariation)) {
      currentCompareKey = this.generateCompareKey(id, selectedCustomization, selectedVariation);
    } else {
      currentCompareKey = countMapState[id].last;
    }
    if (
      (currentCompareKey && !isEmpty(customizeItem) && customizeItem.isCustomizable) ||
      !isEmpty(selectedVariation)
    ) {
      index = items.findIndex(
        (x) =>
          this.generateCompareKey(x.menuItemId, x.orderCustomizations, x.selectedVariation) ===
          currentCompareKey,
      );
    } else {
      index = items.findIndex((x) => x.menuItemId === id);
    }

    if (index > -1) {
      items[index].quantity += 1;
      items[index].addNewItemFlag = addNewItemFlag;
    } else {
      items.push({
        menuItemId: id,
        // customization: get(customizeItem, 'description', ''),
        quantity: 1,
        orderCustomizations: selectedCustomization,
        price,
        variationId: get(selectedVariation, 'id', null),
        selectedVariation: selectedVariation,
        hasVariations: hasVariations,
      });
    }
    if (!currentCompareKey && !isEmpty(customizeItem) && customizeItem.isCustomizable) {
      currentCompareKey = this.generateCompareKey(id, selectedCustomization);
    }
    countMapState[id].quantity += 1;
    countMapState[id].last = currentCompareKey;
    const draftSelectedCustomizationArr = produce(selectedCustomizationArr, (draftState) => {
      if (!isEmpty(selectedCustomization)) {
        const selectedCustomizationArrIndex = selectedCustomizationArr.findIndex(
          (selectedCustomizationObj) => selectedCustomizationObj.id === id,
        );
        if (selectedCustomizationArrIndex > -1) {
          draftState[selectedCustomizationArrIndex].selectedCustomization = selectedCustomization;
        } else {
          draftState.push({
            id,
            selectedCustomization,
          });
        }
      }
    });
    let draftSelectedVariationArr = [];
    if (hasVariations) {
      draftSelectedVariationArr = produce(selectedVariationArr, (draftState) => {
        if (!isEmpty(selectedVariation)) {
          const selectedVariationArrIndex = selectedVariationArr.findIndex(
            (selectedVariationObj) => selectedVariationObj.id === id,
          );
          if (selectedVariationArrIndex > -1) {
            draftState[selectedVariationArrIndex].selectedVariation = selectedVariation;
          } else {
            draftState.push({
              id,
              selectedVariation,
            });
          }
        }
      });
    }

    this.setState({
      countMap: countMapState,
      items,
      addNewItemFlag: false,
      customizeItem: {},
      selectedMenuItemId: id,
      selectedCustomizationArr: draftSelectedCustomizationArr,
      selectedVariationArr: draftSelectedVariationArr,
    });
  };

  generateCompareKey = (id, selectedCustomization, selectedVariation) => {
    let itemIdsArr = [];
    itemIdsArr.push(id);
    if (selectedCustomization) {
      for (const customization of selectedCustomization) {
        itemIdsArr.push(customization.menuCustomizationId);
        sortBy(itemIdsArr);
      }
    }
    if (!isEmpty(selectedVariation)) {
      itemIdsArr.push(selectedVariation.id);
    }
    return join(itemIdsArr, '~');
  };

  onFoodAddClick = (item) => {
    const { countMap } = this.state;
    const { isCustomizable } = item;
    const { restaurant } = this.props;
    let restaurantId = get(restaurant, 'id', '');
    const hasVariations = get(item, 'hasVariations', false);
    if (isCustomizable || hasVariations) {
      if (countMap[item.id] && countMap[item.id].last) {
        this.setState(
          {
            isCustomizable: true,
            openRepeatCustomizationPopup: true,
            customizeItem: { ...item },
            currentItem: item,
            showMenuItemDetailPopup: false,
            selectedMenuItemId: get(item, 'id', ''),
          },
          () => {
            this.props.actions.clearPreviousCustomization().then(() => {
              if (hasVariations) {
                this.props.actions
                  .fetchMenuItemVariations(restaurantId, item.id)
                  .then((response) => {
                    if (!apiResponseCodes.SUCCESS.includes(get(response, 'status'))) {
                      this.setState({
                        showCustomizationErrorCTA: true,
                      });
                    }
                  });
              } else {
                this.props.actions
                  .fetchMenuItemCustomizationsAction(restaurantId, item.id)
                  .then((response) => {
                    if (!apiResponseCodes.SUCCESS.includes(get(response, 'status'))) {
                      this.setState({
                        showCustomizationErrorCTA: true,
                      });
                    }
                  });
              }
            });
          },
        );
      } else {
        this.setState(
          {
            isCustomizable: true,
            customizeItem: { ...item },
            openRepeatCustomizationPopup: false,
            currentItem: item,
            showMenuItemDetailPopup: false,
            selectedMenuItemId: get(item, 'id', ''),
          },
          () => {
            this.props.actions.clearPreviousCustomization().then(() => {
              if (hasVariations) {
                this.props.actions
                  .fetchMenuItemVariations(restaurantId, item.id)
                  .then((response) => {
                    if (!apiResponseCodes.SUCCESS.includes(get(response, 'status'))) {
                      this.setState({
                        showCustomizationErrorCTA: true,
                      });
                    }
                  });
              } else {
                this.props.actions
                  .fetchMenuItemCustomizationsAction(restaurantId, item.id)
                  .then((response) => {
                    if (!apiResponseCodes.SUCCESS.includes(get(response, 'status'))) {
                      this.setState({
                        showCustomizationErrorCTA: true,
                      });
                    }
                  });
              }
            });
          },
        );
      }
    } else {
      this.onAddItem(item);
    }
  };

  onFoodRemoveClick = (item) => {
    const { isCustomizable, id } = item;
    const { countMap, items } = this.state;
    let countMapState = cloneDeep(countMap, true);
    const menuItemIndex = items.findIndex((item) => item.menuItemId === id);
    const customizationItems = filter(items, (result) => result.menuItemId === id);
    if (isCustomizable && customizationItems && customizationItems.length > 1) {
      this.setState({
        showMultipleCustomizationPopup: true,
      });
    } else {
      if (menuItemIndex > -1) {
        let menuItem = items.find((item) => item.menuItemId === id);
        menuItem.quantity = menuItem.quantity - 1;
        if (menuItem.quantity === 0) {
          items.splice(menuItemIndex, 1);
          countMapState[id].last = undefined;
        } else {
          items[menuItemIndex] = menuItem;
        }
        countMapState[id].quantity -= 1;
        this.setState({
          countMap: countMapState,
          items,
        });
      }
    }
  };

  removeUpsellItem = (item) => {
    const { menuItemId: id } = item;
    const { countMap, items } = this.state;
    let countMapState = cloneDeep(countMap, true);
    const menuItemIndex = items.findIndex((item) => item.menuItemId === id);
    if (menuItemIndex > -1) {
      let menuItem = items.find((item) => item.menuItemId === id);
      menuItem.quantity = menuItem.quantity - 1;
      if (menuItem.quantity === 0) {
        items.splice(menuItemIndex, 1);
        countMapState[id].last = undefined;
      } else {
        items[menuItemIndex] = menuItem;
      }
      countMapState[id].quantity -= 1;
      this.setState({
        countMap: countMapState,
        items,
      });
    }
  };

  onAddToCart = ({ selectedCustomization, selectedVariation }) => {
    this.onAddItem(this.state.customizeItem, selectedCustomization, selectedVariation);
    this.onCustomizableHide();
  };

  onAddNewItem = () => {
    const { selectedMenuItemId, selectedCustomizationArr, selectedVariationArr } = this.state;
    const draftSelectedCustomizationArr = produce(selectedCustomizationArr, (draftState) => {
      const selectedCustomizationArrIndex = selectedCustomizationArr.findIndex(
        (selectedCustomizationObj) => selectedCustomizationObj.id === selectedMenuItemId,
      );
      draftState.splice(selectedCustomizationArrIndex, 1); // removing previous selected customizations of menu Item if addNewItemFlag is true
    });
    const draftSelectedVariationArr = produce(selectedVariationArr, (draftState) => {
      const selectedVariationArrIndex = selectedVariationArr.findIndex(
        (selectedVariationObj) => selectedVariationObj.id === selectedMenuItemId,
      );
      draftState.splice(selectedVariationArrIndex, 1); // removing previous selected customizations of menu Item if addNewItemFlag is true
    });
    this.setState({
      openRepeatCustomizationPopup: false,
      addNewItemFlag: true,
      selectedCustomizationArr: draftSelectedCustomizationArr,
      selectedVariationArr: draftSelectedVariationArr,
    });
  };

  onRepeatLastItem = ({ selectedCustomization, selectedVariation }) => {
    const { currentItem } = this.state;
    this.setState(
      {
        openRepeatCustomizationPopup: false,
        isCustomizable: false,
      },
      () => {
        this.onAddItem(currentItem, selectedCustomization, selectedVariation);
      },
    );
  };

  onViewCartClick = () => {
    let deliveryLocation = {};
    const { restaurant, selectedTable, selectedAddress, loginData } = this.props;
    const { items } = this.state;
    const restaurantId = get(restaurant, 'id', '');
    const restaurantTableId = get(selectedTable, 'id', '');
    const guestId = get(loginData, 'id', '');
    selectedAddress && delete selectedAddress.id;
    if (isEqual(get(selectedTable, 'type', ''), 'DL') && !isEmpty(selectedAddress)) {
      deliveryLocation.guestAddress = selectedAddress;
    }
    const cartItems = {
      restaurantId,
      restaurantTableId,
      guestId,
      items,
      deliveryLocation,
      isShowOffers: false,
    };
    this.props.actions.fetchViewCartItemsAction(cartItems).then((response) => {
      if (apiResponseCodes.SUCCESS.includes(get(response, 'status', ''))) {
        this.props.history.push(`/${routes.containers.cart}`);
      }
    });
  };

  handleTrueCallerSignIn = ({ requestId }) => {
    this.props.actions.beginAjaxCallAction();
    this.props.actions.signInByTrueCallerAction({ requestId }).then((response) => {
      if (apiResponseCodes.SUCCESS.includes(get(response, 'status'))) {
        const guestLoginData = get(response, 'data', {});
        this.setState(
          {
            toast: constants.authentication.toastMessage.successfulLogin,
          },
          () => {
            this.props.actions.dispatchGuestLoginDataAction(guestLoginData);
            this.props.actions.endAjaxCallAction();
          },
        );
      } else {
        const code = get(response, 'data.code', '');
        const message = toTitleCase(get(response, 'data.message', ''));
        if (
          isEqual(code, authenticationConfig.userRejected) ||
          isEqual(code, authenticationConfig.trueCallerTimeout) ||
          isEqual(code, authenticationConfig.trueCallerRequestError)
        ) {
          let errorContent = getTrueCallerErrorContent({
            code,
            message,
          });
          this.setState(
            {
              showErrorPopup: true,
              errorContent,
              errorCode: code,
            },
            () => {
              this.props.actions.endAjaxCallAction();
            },
          );
        } else if (isEqual(code, authenticationConfig.requestIdNotFound)) {
          // Again hit the true caller sing in handler
          this.handleTrueCallerSignIn({ requestId });
        } else {
          this.setState(
            {
              showAuthenticationPopup: true,
            },
            () => {
              this.props.actions.endAjaxCallAction();
            },
          );

          // Hide CashCardInfoPopup if it's visible
          !!this.state.showRestaurantCashCardInfoPopup &&
            this.setShowRestaurantCashCardInfoPopup(false);
        }
      }
    });
  };

  handleLoginClick = (event, context = '') => {
    this.setState({ loginInitiatorContext: context });

    const { appSettings, restaurantConfigAdditionalProps } = this.props;
    // first check at restaurant level then check at app level
    const isTrueCallerEnabled = get(
      restaurantConfigAdditionalProps.find((prop) => get(prop, 'code') === 'tc.isEnabled'),
      'value',
      get(appSettings, 'tc.isEnabled', 'false'),
    );
    if (JSON.parse(isTrueCallerEnabled) && !isIOS()) {
      const requestId = generateUUID();
      const trueCallerDeeplink = getTrueCallerDeepLink({ requestId });
      window.location = trueCallerDeeplink;
      setTimeout(() => {
        if (document.hasFocus()) {
          // Truecaller app not present on the device and you redirect the user the regular otp flow
          this.setState({
            showAuthenticationPopup: true,
            visible: false,
          });
          // Hide CashCardInfoPopup if it's visible
          !!this.state.showRestaurantCashCardInfoPopup &&
            this.setShowRestaurantCashCardInfoPopup(false);
        } else {
          // Truecaller app present on the device and the profile overlay opens
          this.setState(
            {
              visible: false,
            },
            () => {
              this.handleTrueCallerSignIn({ requestId });
            },
          );
        }
      }, 600);
    } else {
      this.setState({
        showAuthenticationPopup: true,
        visible: false,
      });
      // Hide CashCardInfoPopup if it's visible
      !!this.state.showRestaurantCashCardInfoPopup &&
        this.setShowRestaurantCashCardInfoPopup(false);
    }
  };

  handleTrueCallerExceptionCTA = () => {
    this.setState({
      showErrorPopup: false,
      errorContent: {
        title: '',
        description: '',
        buttonCta: '',
      },
    });
    this.handleLoginClick();
  };

  onCloseAuthenticationPopup = () => {
    this.setState({
      showAuthenticationPopup: false,
    });
  };

  redirectToOrderList = () => {
    const { history } = this.props;
    history.push(`/${routes.containers.orders}`);
  };

  redirectToGuestCashCardList = () => {
    const { history } = this.props;
    history && history.push(`/${routes.containers.cashCards}`);
  };

  redirectToAboutFudr = () => {
    const { history } = this.props;
    history.push(`/${routes.containers.aboutFudr}`);
  };

  redirectToAboutOutlet = () => {
    const { history, restaurant } = this.props;
    const { id: restaurantId } = restaurant;
    history.push(`/${routes.containers.aboutOutlet}/${restaurantId}`);
  };

  redirectToAccount = () => {
    const { history } = this.props;
    history.push(`/${routes.containers.account}`);
  };

  // redirectToHelpAndSupport = () => {
  //   const { history } = this.props;
  //   history.push(`/${routes.containers.helpAndSupport}`);
  // }

  static getDerivedStateFromProps(
    props,
    {
      originalMenu,
      categories,
      activeItem,
      countMap,
      items,
      loginData,
      showAuthenticationPopup,
      errorContent,
      showErrorPopup,
      showServicesComponent,
      showGenericPopup,
      genericPopupContent,
      genericPopupChildren,
      removableUpsellItemIds,
    },
  ) {
    let newCategories = [...categories];
    let newCountMap = { ...countMap };

    if (!isEmpty(props.loginData) && !isEqual(props.loginData, loginData)) {
      showAuthenticationPopup = false;
    }

    if (
      !isEmpty(props.errorResponse) &&
      ![featureSpecificAPIErrorCodes.otpInvalid].includes(get(props, 'errorResponse.code', ''))
    ) {
      const { code, message, data } = props.errorResponse;
      const subDomain = extractWhitelabelSubdomain();
      // Suppress API error if:
      // - the webapp has been visited from a white-label subdomain as they have been custom-handled in ScanQR component
      // - visited restaurant's cash card does not exist
      // - if the whitelabel app does not exist
      const isAPIErrorSuppressed =
        (subDomain && code === serverErrorCodes.invalidMethodParams) ||
        code === featureSpecificAPIErrorCodes.noRestaurantCashCard ||
        code === serverErrorCodes.notFound;

      if (!isAPIErrorSuppressed) {
        if (isEqual(code, upsellSearchErrorCase.minCartValueError)) {
          const items = get(props, 'viewCartItems.items', []);
          const showUpsell = get(props, 'viewCartItems.showUpsell', false);
          const availedUpsellItems =
            items && items.length > 0 && items.filter((item) => item.itmSrc);
          showGenericPopup = true; // set generic popup state to true
          genericPopupContent = getUpsellErrorPopupContent(code, routes.containers.restaurant);
          genericPopupChildren = (
            <div className="cart_popup-children">
              <div className="min-upsell-amount">
                {`${cartScreenConfig.minimumCartValue} : ${currencies.INR.symbol}${get(
                  showUpsell,
                  'minCartValue',
                )}`}
              </div>
              <div className="loose-deal">{cartScreenConfig.looseDeal}</div>
              <AvailedUpsellItemCard availedUpsellItems={availedUpsellItems} />
            </div>
          );
          removableUpsellItemIds = get(data, 'upsellItemIds', []);
        } else {
          showErrorPopup = true;
          showServicesComponent = false;
          errorContent = {
            title: getErrorTitle({ source: menuScreenConfig, code }),
            description: getErrorDescription({ source: menuScreenConfig, code, message }),
            buttonCta: getButtonHandlerCta({ source: menuScreenConfig, code }),
          };
        }
      }
    }

    // if menu is not empty calculate the headings
    if ((!isEmpty(props.menu) && !isEqual(props.menu, originalMenu)) || isEmpty(items)) {
      const { cart } = props;
      const items = get(props, 'cart.items', []);
      newCategories = reduce(
        props.menu,
        (result, { id, name, categories, menuItems }) => {
          result.push({ id, name });
          // if categories is not empty then populate it
          if (isEmpty(categories) && !isEmpty(menuItems)) {
            for (const item of menuItems) {
              if (item.isEnabled) {
                newCountMap[item.id] = {};
                let selectedItemCount = filter(items, (result) => result.menuItemId === item.id);
                selectedItemCount = getTotalItemCount(selectedItemCount);
                // make zero for default
                newCountMap[item.id].quantity = get(
                  cart,
                  item.id,
                  !isEmpty(items) ? selectedItemCount : 0,
                );
              }
            }
          } else {
            reduce(
              categories,
              (acc, { menuItems }) => {
                if (!isEmpty(menuItems)) {
                  for (const item of menuItems) {
                    if (item.isEnabled) {
                      newCountMap[item.id] = {};
                      let selectedItemCount = filter(
                        items,
                        (result) => result.menuItemId === item.id,
                      );
                      selectedItemCount = getTotalItemCount(selectedItemCount);
                      // make zero for default
                      newCountMap[item.id].quantity = get(
                        cart,
                        item.id,
                        !isEmpty(items) ? selectedItemCount : 0,
                      );
                    }
                  }
                }
                return acc;
              },
              [],
            );
          }
          return result;
        },
        [],
      );
    }
    return {
      categories: [...newCategories],
      originalMenu: props.menu,
      countMap: { ...newCountMap },
      // items: get(props, 'cart.items', items), // TODO: This state update seems unnecessary and is errorsome to repeat order flow. Confirm with Ankit what it was intended for.
      showAuthenticationPopup: showAuthenticationPopup,
      showErrorPopup: showErrorPopup,
      showServicesComponent: showServicesComponent,
      errorContent: errorContent,
      showGenericPopup: showGenericPopup,
      genericPopupContent: genericPopupContent,
      genericPopupChildren: genericPopupChildren,
      removableUpsellItemIds: removableUpsellItemIds,
    };
  }

  calculateCountCost = (countMap, items = []) => {
    const itemPrices = items.map((value, key) => {
      let itemPrice = 0;
      const hasVariations = get(value, 'hasVariations', false);
      if (hasVariations) {
        itemPrice = get(value, 'selectedVariation.price', 0) * value.quantity;
      } else if (value.totalPrice) {
        itemPrice = value.totalPrice;
      } else {
        itemPrice = value.price * value.quantity;
      }

      if (value.orderCustomizations && value.orderCustomizations.length > 0) {
        const customizationPrices = value.orderCustomizations.map((value, key) => {
          return value.price;
        });
        const customizationPrice = reduce(customizationPrices, (a, c) => a + c, 0);
        if (!value.addNewItemFlag) {
          itemPrice += customizationPrice * value.quantity;
        } else {
          itemPrice += customizationPrice;
        }
      }
      return itemPrice;
    });
    const countMapValues = values(countMap);

    this.setState({
      cost: reduce(itemPrices, (a, c) => a + c) || 0,
      count: reduce(
        countMapValues,
        (result, current) => {
          result += current.quantity;
          return result;
        },
        0,
      ),
    });
  };

  renderFoodItem = (menuItem, isMenuReadOnly) => {
    const { disabledCategories } = this.props;
    const { countMap } = this.state;
    const {
      id,
      name,
      price,
      isCustomizable,
      description,
      item,
      type,
      isEnabled,
      menuCategoryDto,
      isRecommended,
      mediaItems,
      imageType,
      hasVariations,
      defaultVariation,
    } = menuItem;
    if (isRecommended) {
      // check if disabledCategories array contains recommended menuItems category and isMenuItemCategory is not enabled
      if (disabledCategories.includes(menuCategoryDto.id) || !menuCategoryDto.isEnabled) {
        return;
      }
    }
    return (
      <FoodItem
        key={id}
        name={name}
        mediaItems={mediaItems}
        price={price}
        isEnabled={isEnabled}
        isCustomizable={isCustomizable}
        hasVariations={hasVariations}
        defaultVariation={defaultVariation}
        isMenuReadOnly={isMenuReadOnly}
        description={description}
        menuItem={item}
        type={type}
        count={countMap[id] && countMap[id].quantity}
        imageType={imageType}
        onFoodAddClick={this.onFoodAddClick}
        onFoodRemoveClick={this.onFoodRemoveClick}
        handleMediaItemClick={this.handleMediaItemClick}
      />
    );
  };

  renderCategory = (categoryItems, isMenuReadOnly) => {
    let pushItem = [];
    const { disabledCategories } = this.props;
    for (const item of categoryItems.menuItems) {
      if (item.isEnabled || disabledCategories.includes(categoryItems.id)) {
        if (disabledCategories.includes(categoryItems.id)) {
          item.isEnabled = false;
        }
        pushItem.push(
          this.renderFoodItem(
            {
              ...item,
              item: { ...item },
            },
            isMenuReadOnly,
          ),
        );
      }
    }
    this.tabRefs[categoryItems.name] = React.createRef();
    return (
      pushItem &&
      pushItem.length > 0 && (
        <div
          ref={this.tabRefs[categoryItems.name]}
          key={categoryItems.id}
          className="restaurant-container_category-item-name-wrapper"
        >
          <div className="restaurant-container_category-item-name">
            {categoryItems.name}
            {categoryItems.description && (
              <Description className="restaurant-container_categoryDescription">
                {categoryItems.description}
              </Description>
            )}
          </div>
          {pushItem}
        </div>
      )
    );
  };

  renderItems = (injectableDOM, injectablePosition, isMenuReadOnly) => {
    const { menu, searchResult, disabledCategories, recommendedSections } = this.props;
    const { scrollContainer, searchValue, showEmptySearchMessage } = this.state;
    let index = 0;
    let itemMenuArr = [];
    if (!isEmpty(searchValue)) {
      let itemMenu =
        searchResult &&
        searchResult.filter(
          (searchItem) =>
            !!searchItem.isEnabled && !disabledCategories.includes(get(searchItem, 'id', '')),
        );
      itemMenuArr =
        disabledCategories &&
        orderBy(itemMenu, (searchItem) => disabledCategories.includes(searchItem.id));
    } else {
      let itemMenu =
        menu &&
        menu.filter(
          (menuItem) =>
            !!menuItem.isEnabled && !disabledCategories.includes(get(menuItem, 'id', '')),
        );
      itemMenuArr =
        disabledCategories &&
        orderBy(itemMenu, (menuItem) => disabledCategories.includes(menuItem.id));
    }
    if (!showEmptySearchMessage) {
      const populatedMenuArray = reduce(
        itemMenuArr,
        (result, { id, name, categories, menuItems, description }) => {
          // create dynamic ref
          this.tabRefs[name] = React.createRef();
          const isRecommendedContainsMenuItems =
            recommendedSections &&
            get(
              recommendedSections.map((item) => isEqual(item.menuItems, menuItems)),
              '0',
              false,
            ); // check whether the menu item are the part of recommended items or not
          if (isRecommendedContainsMenuItems) {
            menuItems = menuItems.filter(
              (item) =>
                item.isEnabled === true &&
                get(item, 'menuCategoryDto.isEnabled', true) === true &&
                get(item, 'menuCategoryDto.parentCategory.isEnabled', true) === true &&
                !disabledCategories.includes(get(item, 'menuCategoryDto.parentCategory.id', '')) &&
                !disabledCategories.includes(get(item, 'menuCategoryDto.id', '')),
            );
            menuItems = orderBy(menuItems, ['mediaItems'], ['asc']);
            const visibleMenuItemsWithImages = menuItems.filter(
              (item) => get(item, 'mediaItems', []).length > 0,
            );
            menuItems = menuItems.map((item, index) =>
              get(item, 'mediaItems', []).length > 0
                ? { ...item, imageType: renderImageType(index, visibleMenuItemsWithImages.length) }
                : item,
            );
          }
          // check if there is a category
          const section = [];
          if (isEmpty(categories) && !isEmpty(menuItems)) {
            for (const item of menuItems) {
              if (item.isEnabled) {
                if (disabledCategories.includes(item.id)) {
                  // check if disable categories contains item id
                  item.isEnabled = false;
                  if (item.isEnabled) {
                    section.push(
                      this.renderFoodItem(
                        {
                          ...item,
                          item: { ...item },
                        },
                        isMenuReadOnly,
                      ),
                    );
                  }
                } else if (disabledCategories.includes(id)) {
                  // check if disable categories contains menu id, then mark isEnabled ==== false
                  item.isEnabled = false;
                  section.push(
                    this.renderFoodItem(
                      {
                        ...item,
                        item: { ...item },
                      },
                      isMenuReadOnly,
                    ),
                  );
                } else if (disabledCategories.includes(item.menuCategoryId)) {
                  item.isEnabled = false;
                  section.push(
                    this.renderFoodItem(
                      {
                        ...item,
                        item: { ...item },
                      },
                      isMenuReadOnly,
                    ),
                  );
                } else {
                  if (item.isEnabled) {
                    section.push(
                      this.renderFoodItem(
                        {
                          ...item,
                          item: { ...item },
                        },
                        isMenuReadOnly,
                      ),
                    );
                  }
                }
              }
            }

            // in empty scrollcontainer do nothing
            if (!isEmpty(scrollContainer)) {
              result.push(
                <WatchComponent
                  key={id}
                  id={id}
                  scrollContainer={scrollContainer}
                  enterViewport={() => {
                    // for 0th index only
                    if (!index) {
                      this.setState({
                        activeItem: name,
                      });

                      // auto scroll to the item in tab
                      scrollElementIntoView(`tab-${name}`);
                    }
                  }}
                  fullyEnterViewport={() => {
                    // for greater than 0th index
                    if (index > 0) {
                      this.setState({
                        activeItem: name,
                      });

                      // auto scroll to the item in tab
                      scrollElementIntoView(`tab-${name}`);
                    }
                  }}
                  exitViewport={() => {
                    const { categories } = this.state;
                    const categoryIndex = findIndex(categories, ['id', id], 1);

                    // select the next item, if it exists
                    if (categories.length - 1 < categoryIndex) {
                      const name = categories[categoryIndex - 1].name;
                      this.setState({
                        activeItem: name,
                      });
                      scrollElementIntoView(`tab-${name}`);
                    }
                  }}
                >
                  <div
                    ref={this.tabRefs[name]}
                    className="restaurant-container_menu-root-section"
                    style={{
                      backgroundColor: getColorIndex(index), // TODO: getColorIndex fn has its definition code commented out. We should remove background color style as well as it's receiving void
                    }}
                  >
                    {section && section.length > 0 && (
                      <>
                        <div className="restaurant-container_categoryName">
                          {name}
                          {description && (
                            <Description className="restaurant-container_categoryDescription">
                              {description}
                            </Description>
                          )}
                        </div>
                        {section}
                        <div className="restaurant-container_line-break" />
                      </>
                    )}
                  </div>
                </WatchComponent>,
              );
              index++;
            }
          } else {
            let categoryItemPush = [];
            if (!isEmpty(categories)) {
              categories
                .filter((categoryItem) => categoryItem.isEnabled)
                .forEach((categoryItem, index, array) => {
                  if (disabledCategories.includes(categoryItem.id)) {
                    categoryItem.isEnabled = false;
                  }

                  const subCategoryItem = categoryItem.menuItems ? (
                    <>
                      {this.renderCategory(categoryItem, isMenuReadOnly)}
                      {index < array.length - 1 && (
                        <Divider classes={{ root: 'sub-category-section-divider' }} />
                      )}
                    </>
                  ) : (
                    []
                  );

                  !isEmpty(subCategoryItem) && categoryItemPush.push(subCategoryItem);
                });
            }

            // in empty scrollcontainer do nothing
            if (!isEmpty(scrollContainer)) {
              categoryItemPush &&
                categoryItemPush.length > 0 &&
                result.push(
                  <WatchComponent
                    key={id}
                    id={id}
                    scrollContainer={scrollContainer}
                    enterViewport={() => {
                      // for 0th index only
                      if (!index) {
                        this.setState({
                          activeItem: name,
                        });
                        // auto scroll to the item in tab
                        scrollElementIntoView(`tab-${name}`);
                      }
                    }}
                    fullyEnterViewport={() => {
                      // for greater than 0th index
                      if (index > 0) {
                        this.setState({
                          activeItem: name,
                        });
                        // auto scroll to the item in tab
                        scrollElementIntoView(`tab-${name}`);
                      }
                    }}
                    exitViewport={() => {
                      const { categories } = this.state;
                      const categoryIndex = findIndex(categories, ['id', id], 1);

                      // select the next item, if it exists
                      if (categories.length - 1 < categoryIndex) {
                        this.setState({
                          activeItem: categories[categoryIndex - 1].name,
                        });
                        // auto scroll to the item in tab
                        scrollElementIntoView(`tab-${name}`);
                      }
                    }}
                  >
                    <div
                      ref={this.tabRefs[name]}
                      style={{
                        backgroundColor: getColorIndex(index), // TODO: getColorIndex fn has its definition code commented out. We should remove background color style as well as it's receiving void
                      }}
                      className="restaurant-container_menu-root-section"
                    >
                      {categoryItemPush && categoryItemPush.length > 0 && (
                        <>
                          <div className="restaurant-container_categoryName">
                            {name}
                            {description && (
                              <Description className="restaurant-container_categoryDescription">
                                {description}
                              </Description>
                            )}
                          </div>
                          {categoryItemPush}
                          <div className="restaurant-container_line-break" />
                        </>
                      )}
                    </div>
                  </WatchComponent>,
                );
            }
            index++;
          }
          return result;
        },
        [],
      );

      // Inject injectableDOM into injectablePosition in populatedMenuArray
      !isEmpty(populatedMenuArray) && populatedMenuArray.length > injectablePosition
        ? populatedMenuArray.splice(injectablePosition, 0, injectableDOM)
        : populatedMenuArray.splice(0, 0, injectableDOM);

      return populatedMenuArray;
    } else {
      return (
        <div className="restaurant-container_empty-search-message">
          {constants.search.noSearchResultText}
        </div>
      );
    }
  };

  // logic for search
  handleSearchChange = (event) => {
    const { value } = event.target;
    const { menu } = this.props;
    let updatedMenu = cloneDeep(menu);
    let sValue = toLower(value.trim()); // modified the user search input to the generic form
    let result = !isEmpty(sValue)
      ? updatedMenu.filter((item) => {
          let found = false;
          if (toLower(item.name).includes(sValue)) {
            // logic checkitem at the menu level aka category
            found = true;
          } else if (
            get(item, 'menuItems', []).findIndex((menuItem) =>
              includes(toLower(menuItem.name), sValue),
            ) > -1
          ) {
            // logic to check item at menu item level
            item.menuItems = get(item, 'menuItems', []).filter((menuItem) =>
              includes(toLower(menuItem.name), sValue),
            );
            found = true;
          } else if (get(item, 'categories', []).length > 0) {
            // logic to check item at category level aka subcategory
            if (
              get(item, 'categories', []).findIndex((categoryItem) =>
                includes(toLower(categoryItem.name), sValue),
              ) > -1
            ) {
              item.categories = item.categories.filter((categoryItem) =>
                includes(toLower(categoryItem.name), sValue),
              );
              found = true;
            } else {
              item.categories = item.categories.filter((category) => {
                if (
                  get(category, 'menuItems', []).findIndex((i) =>
                    toLower(i.name).includes(sValue),
                  ) > -1
                ) {
                  category.menuItems = category.menuItems.filter((menuItem) =>
                    includes(toLower(menuItem.name), sValue),
                  );
                  found = true;
                  return category;
                }
              });
            }
          }
          return found ? item : null;
        })
      : menu;

    if (!isEmpty(result)) {
      this.setState(
        {
          searchValue: value,
          showEmptySearchMessage: false,
        },
        () => {
          this.props.actions.updateMenuAction(result);
        },
      );
    } else {
      this.setState({
        searchValue: value,
        showEmptySearchMessage: true,
      });
    }
  };

  clearSearch = () => {
    this.setState({
      searchValue: '',
      showEmptySearchMessage: false,
    });
  };

  onCloseExceptionComponent = () => {
    this.props.actions.clearApiErrorResponseAction().then(() => {
      this.setState({
        showErrorPopup: false,
        errorContent: {
          title: '',
          description: '',
          buttonCta: '',
        },
      });
    });
  };

  onCloseMultipleCustomizationPopup = () => {
    this.setState({
      showMultipleCustomizationPopup: false,
    });
  };

  handleServicesClick = () => {
    this.props.actions.updateAssistanceStatusAction().then(() => {
      this.setState({
        showServicesComponent: true,
        visible: false,
      });
    });
  };

  handleSwitchRestaurantClick = () => {
    const { restaurantGroup } = this.props;
    const { items, showVerifyCartPopup } = this.state;
    const { code } = restaurantGroup;
    if (!isEmpty(restaurantGroup)) {
      if (showVerifyCartPopup) {
        this.setState(
          {
            showVerifyCartPopup: false,
            visible: false,
          },
          () => {
            this.props.actions.clearViewCartItems().then(() => {
              this.props.history.push(`/${routes.containers.welcome}/${code}`);
            });
          },
        );
      } else {
        if (!isEmpty(items)) {
          this.setState({
            showVerifyCartPopup: true,
            visible: false,
          });
        } else {
          this.setState(
            {
              showVerifyCartPopup: false,
              visible: false,
            },
            () => {
              this.props.actions.clearViewCartItems().then(() => {
                this.props.history.push(`/${routes.containers.welcome}/${code}`);
              });
            },
          );
        }
      }
    } else {
      this.setState({
        showOrderModePopup: true,
        visible: false,
      });
    }
    // On switching order mode
    this.props.actions.clearViewCartItems().then(() => {
      // Clear user's pervious cart
      this.props.actions.dispatchUserCart({});
      // Reset previous orders added to cart in the state
      this.setState({ previousOrdersAddedToCart: [] });
    });
  };

  onCloseServicesComponent = () => {
    this.setState({
      showServicesComponent: false,
    });
  };

  onCloseSelfServicePopup = () => {
    this.setState({
      showSelfPickupPopupComponent: false,
    });
  };

  handleKnowMoreClick = () => {
    this.setState({
      showSelfPickupPopupComponent: true,
    });
  };

  handleExceptionCTA = () => {
    if (isEqual(get(this.state, 'errorCode', ''), authenticationConfig.userRejected)) {
      this.handleTrueCallerExceptionCTA();
      return;
    }
    this.handleButtonCta();
  };

  handleButtonCta = () => {
    const { match, disabledItems } = this.props;
    const { items, countMap, includeMenu } = this.state;
    const restaurantIdAndTableId = get(match, 'params.restaurantAndTableId', '');
    let itemsArr = cloneDeep(items);
    let disabledItemsArr = filter(itemsArr, (item) => disabledItems.includes(item.menuItemId));
    let remainingItemsArr = differenceBy(items, disabledItemsArr, 'menuItemId');
    let newCountMap = cloneDeep(countMap);
    forEach(newCountMap, function (value, key) {
      disabledItems.includes(key) ? (newCountMap[key].quantity = 0) : newCountMap[key]; // eslint-disable-line
    });
    this.props.actions.clearApiErrorResponseAction().then(() => {
      this.setState(
        {
          items: remainingItemsArr,
          countMap: newCountMap,
          errorContent: {},
          showErrorPopup: false,
        },
        () => {
          this.props.actions.clearDisabledMenuItems();
          this.props.actions.clearViewCartItems();
        },
      );
    });
    this.props.actions.fetchRestaurantMenuAction(restaurantIdAndTableId, includeMenu);
  };

  handleLinkClick = () => {
    this.setState({
      showDeliveryAddressPopup: true,
      showSelfPickUpSection: true,
    });
  };

  handleGetItDeliveredClick = () => {
    this.setState({
      showDeliveryAddressPopup: true,
      showSelfPickUpSection: false,
    });
  };

  onCloseDeliveryAddressPopup = () => {
    this.setState({
      showDeliveryAddressPopup: false,
    });
  };

  handleDeliveryAddressCTA = ({ addressObj, isSelfPickUpSelected, isSavedAddressSelected }) => {
    const { selectedTable, serviceTypes, loginData, verifiedLocalities } = this.props;
    const selectedTableType = isSelfPickUpSelected ? 'SP' : 'DL';
    const locationId =
      serviceTypes &&
      get(
        serviceTypes.find((sType) => sType.code === selectedTableType),
        'locationId',
        '',
      );
    const guestId = get(loginData, 'id', '');
    let updateProfilereqBody = {
      guestAddresses: [addressObj],
    };
    if (isSelfPickUpSelected && !isEmpty(locationId)) {
      this.setState(
        {
          showDeliveryAddressPopup: false,
        },
        () => {
          this.props.actions.dispatchPreviousSelectedTable(selectedTable).then(() => {
            this.props.actions.fetchSelectedTable(locationId);
          });
        },
      );
    } else {
      this.setState(
        {
          showDeliveryAddressPopup: false,
        },
        () => {
          if (!isEmpty(loginData)) {
            if (isSavedAddressSelected) {
              if (!isEmpty(locationId)) {
                // check if selfpickup mode is available
                this.props.actions.fetchSelectedTable(locationId).then(() => {
                  this.props.actions.dispatchSelectedAddress(addressObj);
                });
              } else {
                this.props.actions.dispatchSelectedAddress(addressObj);
              }
            } else {
              const localityName = !isEmpty(get(addressObj, 'localityName', ''))
                ? get(addressObj, 'localityName', '')
                : verifiedLocalities &&
                  verifiedLocalities.length > 0 &&
                  get(
                    verifiedLocalities.find((locality) => locality),
                    'localityName',
                    '',
                  ); // find locality obj from verifiedLocalities arr
              if (!isEmpty(locationId)) {
                // check if selfpickup mode is available
                this.props.actions.fetchSelectedTable(locationId).then(() => {
                  this.props.actions
                    .dispatchSelectedAddress({ ...addressObj, localityName })
                    .then(() => {
                      this.props.actions.updateProfileAction(updateProfilereqBody, guestId);
                    });
                });
              } else {
                this.props.actions
                  .dispatchSelectedAddress({ ...addressObj, localityName })
                  .then(() => {
                    this.props.actions.updateProfileAction(updateProfilereqBody, guestId);
                  });
              }
            }
          } else {
            if (!isEmpty(locationId)) {
              // check if selfpickup mode is available
              this.props.actions.fetchSelectedTable(locationId);
            }
          }
        },
      );
    }
  };

  onCloseVerifyCartPopup = () => {
    this.setState({
      showVerifyCartPopup: false,
    });
  };

  onCloseOrderModePopup = () => {
    this.setState({
      showOrderModePopup: false,
    });
  };

  handleServiceModeClick = (service) => {
    const { restaurant, loginData } = this.props;
    const { includeMenu } = this.state;
    const { locationId, code, scanCode } = service;
    const restaurantId = get(restaurant, 'id', '');
    let restaurantOffersReqBody = {
      restaurantId,
      serviceType: code,
      guestId: get(loginData, 'id', ''),
    };
    if (dineInModes.includes(code)) {
      this.setState({
        showOrderModePopup: false,
        showScanQRComponent: true,
      });
    } else {
      this.setState(
        {
          showOrderModePopup: false,
          items: [],
        },
        () => {
          this.props.actions.fetchSelectedTable(locationId).then(() => {
            this.props.actions.fetchRestaurantMenuAction(scanCode, includeMenu).then(() => {
              //fetch restaurant offers if service mode is updated
              this.props.actions
                .fetchRestaurantOffersAction({ restaurantOffersReqBody })
                .then(() => {
                  this.props.history.push(`/${routes.containers.restaurant}/${scanCode}`);
                });
            });
          });
        },
      );
    }
  };

  handleMediaItemClick = ({ mediaItems, menuItem }) => {
    this.setState({
      showMenuItemDetailPopup: true,
      mediaItems,
      menuItem,
    });
  };

  onCloseMenuItemDetailsPopup = () => {
    this.setState({
      showMenuItemDetailPopup: false,
    });
  };

  handleViewOfferDetailsClick = (offer) => {
    this.setState({
      showOfferDetailsPopup: true,
      offer,
    });
  };

  onCloseOfferDetailsPopup = () => {
    this.setState({
      showOfferDetailsPopup: false,
      offer: {},
    });
  };

  handleCloseScanQR = () => {
    this.setState({
      showScanQRComponent: false,
    });
  };

  onOpenPWAInstallPopup = () => {
    this.setState({ showPWAInstallPopup: true });
  };

  onClosePWAInstallPopup = () => {
    this.setState({ showPWAInstallPopup: false });
  };

  setShowSafariPWAInstallPopup = (showPopup = false) => {
    this.setState({ showSafariPWAInstallPopup: showPopup, visible: false });
  };

  handleSafariPWAInstallClick = (whitelabelInfoExists, showPopup = false) => {
    const isPWADirectlyInstallable = whitelabelInfoExists;
    isPWADirectlyInstallable ? this.setShowSafariPWAInstallPopup(showPopup) : this.openGetPWALink();
  };

  handleInstallPWAClick = (whitelabelInfoExists) => {
    this.setState(
      {
        visible: false, // Hide sidebar
      },
      () => {
        const isPWADirectlyInstallable = whitelabelInfoExists;
        isPWADirectlyInstallable ? this.props.handleInstallPWAClick() : this.openGetPWALink();
      },
    );
  };

  openGetPWALink = () => {
    const appLink = get(this.props, 'progressiveWebApp.appLink', '');
    appLink && window.open(appLink, '_blank').focus(); // Open outlet's get-app link in a new tab
  };

  handleGuestCashCardBannerClick = (guestCashCardId) => {
    const { history } = this.props;
    history && history.push(`/${routes.containers.cashCards}/${guestCashCardId}`);
  };

  getDoodleUrl = () => {
    let doodleUrl = '';
    const { restaurant, appSettings } = this.props;
    const menuDoodle = get(appSettings, 'client.doodle.menu', '');
    const restaurantMenuDoodle = get(
      get(restaurant, 'restaurantConfig.additionalProperties', []).find(
        (doodle) => doodle.code === 'client.doodle.menu',
      ),
      'value',
      '',
    );
    doodleUrl = !isEmpty(restaurantMenuDoodle) ? restaurantMenuDoodle : menuDoodle;
    return doodleUrl;
  };

  getDoodleLink = () => {
    let doodleLink = '';
    const { restaurant, appSettings } = this.props;
    const menuDoodleLink = get(appSettings, 'doodle.link', '');
    const restaurantMenuDoodleLink = get(
      get(restaurant, 'restaurantConfig.additionalProperties', []).find(
        (doodle) => doodle.code === 'doodle.link',
      ),
      'value',
      '',
    );
    doodleLink = !isEmpty(restaurantMenuDoodleLink) ? restaurantMenuDoodleLink : menuDoodleLink;
    return doodleLink;
  };

  onRestaurantCashCardClick = () => {
    this.setShowRestaurantCashCardInfoPopup(true);
  };

  onRestaurantCashCardKnowMoreClick = () => {
    this.setShowRestaurantCashCardInfoPopup(true);
  };

  onRestaurantCashCardRechargeClick = () => {
    this.initiateCashCardRechargeFlow();
    this.setShowRestaurantCashCardInfoPopup(false);
  };

  initiateCashCardRechargeFlow = () => {
    this.setShowRechargeOngoingScreen(true);
  };

  handleRestaurantCashCardInfoPopupClose = () => {
    this.setShowRestaurantCashCardInfoPopup(false);
  };

  setShowRestaurantCashCardInfoPopup = (flag) => {
    this.setState({
      showRestaurantCashCardInfoPopup: flag,
    });
  };

  setHasGuestIssuedRestaurantCashCard = (flag) => {
    this.setState({
      hasGuestIssuedRestaurantCashCard: flag,
    });
  };

  setCartState = (cart) => {
    this.setState({
      cart,
    });
  };

  renderRestaurantCashCardPreview = (restaurantCashCardInfo) => {
    return (
      <div className="restaurant-container_cash-card-wrapper">
        <CashCardPreview
          cardName={restaurantCashCardInfo.title}
          backgroundColor={restaurantCashCardInfo.colour}
          brandLogo={restaurantCashCardInfo.logo}
          isBackgroundBlended={restaurantCashCardInfo.isBackgroundBlendedWithLogo}
          onCardClick={this.onRestaurantCashCardClick}
          onKnowMoreClick={this.onRestaurantCashCardKnowMoreClick}
        />
      </div>
    );
  };

  handleRechargeConfirmationScreenClose = () => {
    // Clear the lastCashCardRechargeTransaction from redux store state
    const {
      lastCashCardRechargeTransaction,
      actions: { dispatchLastCashCardRechargeTransaction },
    } = this.props;
    !isEmpty(lastCashCardRechargeTransaction) && dispatchLastCashCardRechargeTransaction({});

    this.setShowRechargeConfirmationScreen(false);
  };

  handleRechargeOngoingScreenClose = () => {
    this.setShowRechargeOngoingScreen(false);
  };

  setShowRechargeConfirmationScreen = (flag) => {
    this.setState({
      showRechargeConfirmationScreen: flag,
    });
  };

  setShowRechargeOngoingScreen = (flag) => {
    this.setState({
      showRechargeOngoingScreen: flag,
    });
  };

  onCloseGenericPopupComponent = () => {
    this.setState({
      showGenericPopup: false,
      genericPopupChildren: '',
      genericPopupContent: '',
    });
    this.props.actions.clearApiErrorResponseAction();
  };

  handleLoseDealAndProceedCTA = () => {
    let deliveryLocation = {};
    const { restaurant, selectedTable, selectedAddress, loginData } = this.props;
    const { items, removableUpsellItemIds } = this.state;
    const restaurantId = get(restaurant, 'id', '');
    const restaurantTableId = get(selectedTable, 'id', '');
    const guestId = get(loginData, 'id', '');
    selectedAddress && delete selectedAddress.id;
    if (isEqual(get(selectedTable, 'type', ''), 'DL') && !isEmpty(selectedAddress)) {
      deliveryLocation.guestAddress = selectedAddress;
    }
    // remove upsellItemId's which are included in removableUpsellItemIds arr
    const updatedItems = items.filter(
      (item) => !removableUpsellItemIds.includes(item.upsellItemId),
    );
    // if cart contains items after removing upsell items then proceed to cart else close the popup and stay on the menu screen

    if (updatedItems && updatedItems.length > 0) {
      const cartItems = {
        restaurantId,
        restaurantTableId,
        guestId,
        items: updatedItems,
        deliveryLocation,
        isShowOffers: false,
      };
      this.props.actions.fetchViewCartItemsAction(cartItems).then((viewCartItemsResponse) => {
        if (apiResponseCodes.SUCCESS.includes(get(viewCartItemsResponse, 'status', ''))) {
          this.onCloseGenericPopupComponent();
          this.props.history.push(`/${routes.containers.cart}`);
        }
      });
    } else {
      this.removeUpsellItem(items[0]);
      this.onCloseGenericPopupComponent();
    }
  };

  handleCustomizationErrorCTA = () => {
    const { restaurant } = this.props;
    const { currentItem } = this.state;
    let restaurantId = get(restaurant, 'id', '');
    this.setState(
      {
        showCustomizationErrorCTA: false,
      },
      () => {
        this.props.actions
          .fetchMenuItemCustomizationsAction(restaurantId, currentItem.id)
          .then((response) => {
            if (!apiResponseCodes.SUCCESS.includes(get(response, 'status'))) {
              this.setState({
                showCustomizationErrorCTA: true,
              });
            }
          });
      },
    );
  };

  // Check if the menu category (main and sub) is available as per the category rules and enabled
  isMenuCategoryAvailable = (menuCategory = {}, parentMenuCategory = {}) => {
    const isSelfScopedMenuCategoryAvailable = (category) =>
      get(category, 'isEnabled', false) &&
      !this.props.disabledCategories.includes(get(category, 'id'));

    const hasParentMenuCategory = !isEmpty(parentMenuCategory);
    if (!hasParentMenuCategory) return isSelfScopedMenuCategoryAvailable(menuCategory); // does not have parent category

    // has parent category, so check for its availability
    return (
      isSelfScopedMenuCategoryAvailable(menuCategory) &&
      isSelfScopedMenuCategoryAvailable(parentMenuCategory)
    );
  };

  // Extract available menu items from the Menu data fetched through API into redux store
  extractAvailableMenuItemsIds = () => {
    const { menu } = this.props;
    const availableMenuCategoriesWithMenuItems = [];
    let availableItemsIdsAggregator = [];

    if (!isEmpty(menu)) {
      menu.forEach((mainCategory) => {
        if (!get(mainCategory, 'id') || !this.isMenuCategoryAvailable(mainCategory)) return; // Do nothing if the main category is unavailable or is recommended category (which has no id)

        // If sub-categories are present, push the available ones into availableMenuCategoriesWithMenuItems array
        const subCategories = get(mainCategory, 'categories', []);

        if (!isEmpty(subCategories)) {
          subCategories.forEach((subCategory) => {
            this.isMenuCategoryAvailable(subCategory) &&
              availableMenuCategoriesWithMenuItems.push(subCategory);
          });
        } else {
          // Else, push the main category
          availableMenuCategoriesWithMenuItems.push(mainCategory);
        }
      });

      availableMenuCategoriesWithMenuItems.forEach((category) => {
        const availableMenuItemsIds = get(category, 'menuItems', []).map(
          (menuItem) => get(menuItem, 'isEnabled') && get(menuItem, 'id'),
        );
        availableItemsIdsAggregator = [...availableItemsIdsAggregator, ...availableMenuItemsIds];
      });
    }

    return uniq(availableItemsIdsAggregator);
  };

  // Menu items would be unavailable in repeat orders if either they are disabled, or not available based on category rules or don't have an attached menu customization enabled
  extractUnavailableMenuItemsForRepeatOrders = (itemsToCheck = []) => {
    const allAvailableItemsIdArray = this.extractAvailableMenuItemsIds();
    const unavailableOrderItems = itemsToCheck.filter(
      (item) =>
        !allAvailableItemsIdArray.includes(get(item, 'id')) ||
        get(item, 'menuCustomizations', []).some(
          (menuCustomization) => !get(menuCustomization, 'isEnabled'),
        ),
    );

    return unavailableOrderItems;
  };

  // If all of the items from the previous are unavailable, then the previous order should not be available itself
  checkPreviousOrderAvailability = (previousOrder) =>
    this.extractUnavailableMenuItemsForRepeatOrders(get(previousOrder, 'menuItems', [])).length !==
    get(previousOrder, 'menuItems', []).length;

  renderPreviousOrderCards = (previousOrders = []) =>
    previousOrders
      .map(
        (orderObj, index) =>
          this.checkPreviousOrderAvailability(orderObj) && (
            <PreviousOrderCard
              key={index}
              order={orderObj}
              previousOrdersAddedToCart={this.state.previousOrdersAddedToCart}
              onViewDetails={(order) => this.setState({ selectedPreviousOrder: order })}
              onAddToCart={this.handleCardAddPreviousOrderToCart}
              isMenuReadOnly={this.isMenuReadOnly()}
            />
          ),
      )
      .filter(Boolean); // filter out any falsy values

  handleCardAddPreviousOrderToCart = (order) => {
    const previousOrderItems = get(order, 'menuItems', []);

    const unavailableOrderItems =
      this.extractUnavailableMenuItemsForRepeatOrders(previousOrderItems);

    // If all items from the repeat order available, then directly add them to cart, else open the Previous Order Details popup first
    if (isEmpty(unavailableOrderItems)) {
      this.setState(
        (prevState) => ({
          previousOrdersAddedToCart: [...prevState.previousOrdersAddedToCart, get(order, 'id', '')],
        }),
        () => {
          this.handleAddPreviousOrderItems(previousOrderItems);
        },
      );
    } else {
      this.setState({ selectedPreviousOrder: order });
    }
  };

  handlePopupAddPreviousOrderToCart = (order) => {
    const previousOrderItems = get(order, 'menuItems', []);

    const unavailableOrderItems =
      this.extractUnavailableMenuItemsForRepeatOrders(previousOrderItems);

    this.setState(
      (prevState) => ({
        selectedPreviousOrder: {},
        previousOrdersAddedToCart: [...prevState.previousOrdersAddedToCart, get(order, 'id', '')],
      }),
      () => {
        this.handleAddPreviousOrderItems(previousOrderItems, unavailableOrderItems);
      },
    );
  };

  handleAddPreviousOrderItems = (previousOrderItems = [], unavailableOrderItems = []) => {
    let draftCountMap = JSON.parse(JSON.stringify(this.state.countMap));
    let draftTotalOrderItems = JSON.parse(JSON.stringify(this.state.items));

    const availableOrderItems = previousOrderItems.filter(
      (item) =>
        !unavailableOrderItems.some(
          (unavailableItems) => get(unavailableItems, 'id', '') === get(item, 'id'),
        ),
    );

    availableOrderItems.forEach((item) => {
      const targetMenuItemId = get(item, 'id', '');

      // Add the item quantity from the previous order to the countMap state
      if (draftCountMap[targetMenuItemId])
        draftCountMap[targetMenuItemId].quantity += get(item, 'quantity', 0);

      const targetLineItemIndexInCart = this.state.items.findIndex((cartLineItem) =>
        this.isSameCartLineItem(item, cartLineItem),
      );

      // If item is already in the draft cart, increase its quantity as per the items from the previous order, else, just add it to the draft cart
      if (targetLineItemIndexInCart > -1) {
        draftTotalOrderItems[targetLineItemIndexInCart] = {
          menuItemId: targetMenuItemId,
          // customization: get(item, 'description', ''),
          quantity:
            draftTotalOrderItems[targetLineItemIndexInCart].quantity + get(item, 'quantity', 0),
          orderCustomizations: draftTotalOrderItems[targetLineItemIndexInCart].orderCustomizations,
          price: get(item, 'price', 0),
        };
      } else {
        const orderCustomizations = [];

        get(item, 'menuCustomizations', []).forEach((customization) => {
          get(customization, 'isEnabled') && // double check that the menu customization is available
            orderCustomizations.push({
              menuCustomizationId: get(customization, 'id', ''),
              price: get(customization, 'price', 0),
              type: get(customization, 'type'),
            });
        });

        draftTotalOrderItems.push({
          menuItemId: targetMenuItemId,
          // customization: get(item, 'description', ''),
          quantity: get(item, 'quantity', 0),
          orderCustomizations,
          price: get(item, 'price', 0),
        });
      }
    });

    this.setState({ items: draftTotalOrderItems, countMap: draftCountMap });
  };

  // Check if the target line item is the same cart line item i.e. has the same menu item with the same customizations applied
  isSameCartLineItem = (targetLineItem = {}, cartLineItem = {}) => {
    const isSameMenuItem = targetLineItem.id === cartLineItem.menuItemId;

    if (!isSameMenuItem) return false; // if the line items constitute of different menu items, return false

    const targetLineItemCustomizationIds = get(targetLineItem, 'menuCustomizations', [])
      .map((customization) => get(customization, 'isEnabled') && get(customization, 'id')) // check for enabled menu customizations
      .filter(Boolean); // filter out falsy values
    const cartLineItemCustomizationIds = get(cartLineItem, 'orderCustomizations', [])
      .map((customization) => get(customization, 'menuCustomizationId'))
      .filter(Boolean); // filter out falsy values

    // If both the line items have no customizations, skip the "have same customizations applied" check
    if (isEmpty(targetLineItemCustomizationIds) && isEmpty(cartLineItemCustomizationIds))
      return isSameMenuItem;

    const haveSameMenuCustomizationsApplied = areSameArraysSansOrder(
      targetLineItemCustomizationIds,
      cartLineItemCustomizationIds,
    );

    return isSameMenuItem && haveSameMenuCustomizationsApplied;
  };

  isMenuReadOnly = () =>
    JSON.parse(
      get(
        this.props.restaurantConfigAdditionalProps.find(
          (prop) => get(prop, 'code') === 'client.menu.readOnly',
        ),
        'value',
        'false',
      ),
    );

  handleStopTracking = () => {
    this.setState({ trackAnalyticEvent: false });
  };

  setShowInstallAppPopup = (flag) => {
    this.setState({
      showInstallAppPopup: flag,
    });
  };

  handleInstallAppClick = () => {
    const { appStartupInfo } = this.props;
    const subDomain = get(appStartupInfo, 'subdomain', '');
    if (!isEmpty(subDomain)) {
      const getAppLink = `https://${subDomain}.fudr.in/${rewardsRoutes.rewards}/${rewardsRoutes.containers.getApp}`;
      this.setShowInstallAppPopup(true);
      this.setState({
        getAppLink,
      });
    }
  };

  componentWillUnmount = () => {
    this.props.actions.clearApiErrorResponseAction();

    this.props.actions.clearRestaurantCashCard();

    // Clear the lastCashCardRechargeTransaction from redux store state
    !isEmpty(this.props.lastCashCardRechargeTransaction) &&
      this.props.actions.dispatchLastCashCardRechargeTransaction({});

    this.props.actions.clearProgressiveWebAppData();

    // Clear the previous orders from redux store
    !isEmpty(this.props.previousOrders) && this.props.actions.clearGuestPreviousOrders();
  };

  static contextType = DeviceContext;

  render() {
    const {
      visible,
      isCustomizable,
      activeItem,
      cost,
      count,
      customizeItem,
      selectedMenuItemId,
      selectedCustomizationArr,
      openRepeatCustomizationPopup,
      showMultipleCustomizationPopup,
      showAuthenticationPopup,
      showModal,
      searchValue,
      showErrorPopup,
      errorContent,
      showServicesComponent,
      showEmptySearchMessage,
      showSelfPickupPopupComponent,
      showDeliveryAddressPopup,
      showVerifyCartPopup,
      showOrderModePopup,
      page,
      showSelfPickUpSection,
      showMenuItemDetailPopup,
      mediaItems,
      menuItem,
      countMap,
      showOfferDetailsPopup,
      offer,
      showScanQRComponent,
      banner,
      showPWAInstallPopup,
      showSafariPWAInstallPopup,
      showRestaurantCashCardInfoPopup,
      noOffersToast,
      hasGuestIssuedRestaurantCashCard,
      loginInitiatorContext,
      toast,
      showRechargeConfirmationScreen,
      showRechargeOngoingScreen,
      showGenericPopup,
      genericPopupChildren,
      genericPopupContent,
      showCustomizationErrorCTA,
      selectedPreviousOrder,
      previousOrdersAddedToCart,
      loadingPreviousOrder,
      geoLocationPermissionStatus,
      trackAnalyticEvent,
      isMenuEmpty,
      showInstallAppPopup,
      getAppLink,
      selectedVariationArr,
    } = this.state;

    const {
      selectedTable,
      restaurant,
      history,
      location,
      menu,
      disabledCategories,
      isAssistanceUpdated,
      loginData,
      verifiedLocalities,
      serviceTypes,
      previousSelectedTable,
      selectedAddress,
      restaurantGroup,
      restaurantOffers,
      pwaFeatureEnabled,
      enablePWAInstallWidgets,
      appStartupInfo,
      restaurantCashCard,
      guestCashCards,
      lastCashCardRechargeTransaction,
      cashCardFeatureEnabled,
      progressiveWebApp,
      previousOrders,
    } = this.props;

    const isGuestLoggedIn = !isEmpty(loginData);

    const { type, tableTitle, tableLabel } = selectedTable;
    const itemMenu = menu && menu.filter((menuItem) => menuItem.isEnabled === true);
    const itemMenuArr =
      disabledCategories &&
      orderBy(itemMenu, (menuItem) => disabledCategories.includes(menuItem.id));
    const offers = restaurantOffers &&
      get(restaurantOffers, 'offers', []) &&
      get(restaurantOffers, 'offers', []).length > 0 && [
        ...get(restaurantOffers, 'offers', []).filter(
          (offer) => shouldOfferBeVisibleOnMenu(offer) && !!offer.isGuestSpecific, // Need to show guest-specific offers first in the list
        ),
        ...get(restaurantOffers, 'offers', []).filter(
          (offer) => shouldOfferBeVisibleOnMenu(offer) && !offer.isGuestSpecific,
        ),
      ];
    const serviceTypeStatus =
      serviceTypes &&
      serviceTypes.length > 0 &&
      get(
        serviceTypes.find((sType) => get(sType, 'code', '') === get(selectedTable, 'type', '')),
        'status',
        '',
      );
    const restaurantMode = get(restaurant, 'restaurantConfig.mode', '');
    const restaurantOperatingMode = getRestaurantOperatingMode(restaurantMode, serviceTypeStatus);

    // PWA install related variables
    let restaurantEntityName = '';
    let restaurantEntityImage = '';
    let showPWAInstallCardFlag = false;
    let showPWAInstallPopupFlag = false;
    let showSafariPWAInstallPopupFlag = false;
    let whitelabelInfoExists = false;
    let whitelabelAppDataExists = false;

    let deviceType = this.context; // Get device type from App component's DeviceContext
    const browserInfo = detectWebAppBrowser();
    const isSafari = !isEmpty(browserInfo) && browserInfo['name'] === browserList.iOSSafari;

    if (pwaFeatureEnabled) {
      const isRestaurantGroupScope = get(appStartupInfo, 'subdomainObj.isGroup', false);
      whitelabelInfoExists = !isEmpty(appStartupInfo);

      const deviceRendersMenuPWAWidget =
        devicesRenderingMenuPWAWidgetsList.includes(deviceType) &&
        enablePWAInstallWidgets &&
        (deviceType === 'ios'
          ? isSafari && !isInStandaloneMode() // Currently, PWAs on iOS are only suppported in Safari. isStandaloneMode helps identify whether the webapp is running as a PWA on iOS.
          : true);
      const subDomain = extractWhitelabelSubdomain();
      whitelabelAppDataExists = subDomain
        ? !isEmpty(get(appStartupInfo, 'whiteLabelApp'))
        : !isEmpty(progressiveWebApp);
      const pwaDiscoveryCriteria =
        (subDomain ? whitelabelInfoExists : true) && whitelabelAppDataExists; // Ensure the whitelabel info and whitelabel app data exists as a criteria to make PWA widgets visible
      const isRewardAppAvailable = isEqual(get(appStartupInfo, 'rewardProgram.status'), 'ACTIVE');

      showPWAInstallCardFlag =
        pwaDiscoveryCriteria && deviceRendersMenuPWAWidget && !isRewardAppAvailable;
      showPWAInstallPopupFlag =
        showPWAInstallPopup &&
        pwaDiscoveryCriteria &&
        deviceRendersMenuPWAWidget &&
        !isRewardAppAvailable;
      showSafariPWAInstallPopupFlag = showSafariPWAInstallPopup && deviceRendersMenuPWAWidget;

      // Extract whitelabel app related data
      const { appName, appIconUrl } = subDomain
        ? get(appStartupInfo, 'whiteLabelApp', {})
        : progressiveWebApp;

      // Use app name if has been provided by brand admin, else use restaurant's/group's name
      restaurantEntityName = !isEmpty(appName)
        ? appName
        : isRestaurantGroupScope
        ? get(restaurantGroup, 'name', '')
        : get(restaurant, 'name', '');

      // Use app image if has been provided by brand admin, else use restaurant's/group's image
      restaurantEntityImage = !isEmpty(appIconUrl)
        ? appIconUrl
        : isRestaurantGroupScope
        ? ''
        : get(restaurant, 'restaurantConfig.logoUrl', '');
    }

    const doodleUrl = this.getDoodleUrl();
    const doodleLink = this.getDoodleLink();

    // Cash card related variables
    let showRestaurantCashCardFlag = false;
    let showGuestCashCardBannerFlag = false;

    let guestCashCardInfo = {};
    let restaurantCashCardInfo = {};

    if (cashCardFeatureEnabled) {
      const [guestCashCardObject] = guestCashCards; // Extract singular guest cash card
      guestCashCardInfo = {
        id: get(guestCashCardObject, 'id', ''),
        balance: get(guestCashCardObject, 'balance', ''),
        title: get(guestCashCardObject, 'cashCard.title', ''),
        colour: get(guestCashCardObject, 'cashCard.colour', ''),
        isPaused: get(guestCashCardObject, 'cashCard.isPaused', ''),
        email: get(guestCashCardObject, 'cashCard.cashCardRestaurants[0].email', ''),
      };

      const [restaurantCashCardObject] = restaurantCashCard; // Extract singular outlet cash card
      restaurantCashCardInfo = {
        id: get(restaurantCashCardObject, 'id', ''),
        title: get(restaurantCashCardObject, 'title', ''),
        colour: get(restaurantCashCardObject, 'colour', ''),
        isBackgroundBlendedWithLogo: get(restaurantCashCardObject, 'isBgBlend', ''),
        isPaused: get(restaurantCashCardObject, 'isPaused', false),
        minAmount: get(restaurantCashCardObject, 'minAmount', ''),
        logo: get(restaurantCashCardObject, 'iconUrl', ''),
        cashCardRestaurants: get(restaurantCashCardObject, 'cashCardRestaurants', []),
      };

      // If the guest cash card data is non-empty, cash card is not paused at all or paused with non-zero balance, then only should the it be available for use
      showGuestCashCardBannerFlag =
        hasGuestIssuedRestaurantCashCard &&
        !isEmpty(guestCashCards) &&
        (!guestCashCardInfo.isPaused ? true : guestCashCardInfo.balance > 0);
      // If the guest has not issued a restaurant cash card yet, and restaurant cash card data is non-empty, plus it's not paused, show the restaurant cash card preview
      showRestaurantCashCardFlag =
        !hasGuestIssuedRestaurantCashCard &&
        !isEmpty(restaurantCashCard) &&
        !restaurantCashCardInfo.isPaused;
    }

    const showMenuButton = isEmpty(searchValue.trim()); // Show the menu button (which open menu modal containing categories) only when the search is empty

    const rechargeTransactionObject = {
      amount: get(lastCashCardRechargeTransaction, 'amount', ''),
      id: get(lastCashCardRechargeTransaction, 'id', ''),
      initiateDate: get(lastCashCardRechargeTransaction, 'initiateDate', ''),
      type: get(lastCashCardRechargeTransaction, 'type', ''),
      status: get(lastCashCardRechargeTransaction, 'status', ''),
    };

    // Previous orders related implementation
    const previousOrderCardsDOMArray = this.renderPreviousOrderCards(previousOrders);

    const isMenuReadOnly = this.isMenuReadOnly();

    const isRewardAppAvailable = isEqual(get(appStartupInfo, 'rewardProgram.status'), 'ACTIVE');

    return (
      <>
        {showScanQRComponent && (
          <ScanQR history={history} handleCloseScanQR={this.handleCloseScanQR} />
        )}
        {visible && (
          <Drawer
            identifier={routes.containers.restaurant}
            visible={visible}
            onHide={this.onHide.bind(this)}
            onLogin={this.handleLoginClick.bind(this)}
            handleMenuClick={this.handleMenuClick}
            redirectToOrderList={this.redirectToOrderList.bind(this)}
            redirectToGuestCashCardList={this.redirectToGuestCashCardList}
            history={history}
            redirectToAboutFudr={this.redirectToAboutFudr.bind(this)}
            redirectToAboutOutlet={this.redirectToAboutOutlet.bind(this)}
            redirectToAccount={this.redirectToAccount.bind(this)}
            // redirectToHelpAndSupport={this.redirectToHelpAndSupport.bind(this)}
            handleInstallPWAClick={() => this.handleInstallPWAClick(whitelabelInfoExists)}
            handleSafariPWAInstallClick={(show) =>
              this.handleSafariPWAInstallClick(whitelabelInfoExists, show)
            }
            pwaFeatureEnabled={pwaFeatureEnabled}
            enablePWAInstallWidgets={enablePWAInstallWidgets}
            handleSwitchClick={this.handleSwitchRestaurantClick}
            cashCardFeatureEnabled={cashCardFeatureEnabled}
          />
        )}
        {/* app header goes here */}
        <div className="restaurant-container_drawer" onClick={this.onHide.bind(this)}>
          <Header
            isMenuEmpty={isMenuEmpty}
            handleMenuClick={this.handleMenuClick}
            restaurant={restaurant}
            identifier={routes.containers.restaurant}
            visible={visible}
            handleSearchChange={this.handleSearchChange}
            clearSearch={this.clearSearch}
            searchValue={searchValue}
            handleServicesClick={this.handleServicesClick.bind(this)}
            handleSwitchRestaurantClick={this.handleSwitchRestaurantClick}
            tableType={type}
            label={getTableLabel(type, tableTitle, tableLabel)}
            restaurantGroup={restaurantGroup}
            serviceTypes={serviceTypes}
            isMenuReadOnly={isMenuReadOnly}
          />

          {!isMenuEmpty && showGuestCashCardBannerFlag && (
            <Banner
              className="cash-card-banner"
              color={guestCashCardInfo.colour}
              onClick={() => this.handleGuestCashCardBannerClick(guestCashCardInfo.id)}
              message={
                <>
                  <span className="cash-card-name">{`${guestCashCardInfo.title} ${menuScreenConfig.cashCardBanner.titleSuffix}`}</span>
                  <span className="cash-card-actions">
                    <span className="cash-card-balance-group">
                      <span className="cash-card-balance">{guestCashCardInfo.balance}</span>
                      <span className="coin-symbol">
                        <img src={pointCoinSymbol} alt="" />
                      </span>
                    </span>
                    <span className="cash-card-view-detail">
                      <img src={forwardArrowIcon} alt="" />
                    </span>
                  </span>
                </>
              }
            />
          )}

          {/* Logic for Side bar Menu */}
          <div
            className="restaurant-container_overlay"
            style={{
              pointerEvents: visible ? 'all' : 'none',
              opacity: visible ? 1 : 0,
            }}
            onClick={this.onHide.bind(this)}
          />
          <div
            ref={this.scrollRef}
            className={classNames('restaurant-container', {
              'cash-card-banner-offset': showGuestCashCardBannerFlag,
            })}
          >
            {/* request assistance success toast message */}
            {isAssistanceUpdated && (
              <div
                className="restaurant-container_request-assistance-success"
                id="assistance-success"
              >
                {constants.services.toastMessage}
              </div>
            )}
            {/* Feeback toast to show in case no offers are available when guest logins through GuestExclusiveOffers banner */}
            {!isMenuEmpty &&
              !isEmpty(noOffersToast) &&
              loginInitiatorContext === authInitiatorFeatures.offer && (
                <Toast content={noOffersToast} />
              )}
            {!isEmpty(toast) && <Toast content={toast} />}

            {/* this bottom content will be scrollable */}
            {/* place holder for menu doodle image */}

            {!isMenuEmpty && !isEmpty(doodleUrl) && (
              <ImageHybrid
                classes="restaurant-container_doodle"
                sourceUrl={doodleUrl}
                hyperLink={doodleLink}
              />
            )}

            {!isMenuEmpty && showPWAInstallCardFlag && !isRewardAppAvailable && (
              <InstallAppetizerCard
                restaurantEntityName={restaurantEntityName}
                restaurantEntityImage={restaurantEntityImage}
                handleInstallClick={() =>
                  isSafari
                    ? this.handleSafariPWAInstallClick(whitelabelInfoExists, true)
                    : this.handleInstallPWAClick(whitelabelInfoExists)
                }
                handleBrandClick={this.onOpenPWAInstallPopup}
              />
            )}
            {isRewardAppAvailable && (
              <InstallAppCard
                appStartupInfo={appStartupInfo}
                onInstallAppClick={this.handleInstallAppClick}
              />
            )}
            {/* Self Pick Card section */}
            {/* commenting selfpickup card section */}
            {/* {(isEqual(get(previousSelectedTable, 'type', ''), 'DL') || isEqual(type, 'SP')) &&
              isEmpty(searchValue) && (
                <SelfPickupCard handleKnowMoreClick={this.handleKnowMoreClick.bind(this)} />
              )} */}
            {/* Delivery Card section */}
            {!isMenuEmpty &&
              (isEqual(type, 'DL') || isEqual(get(previousSelectedTable, 'type', ''), 'DL')) && (
                <DeliveryCard
                  loginData={loginData}
                  page={page}
                  restaurantCity={startCase(get(restaurant, 'city', constants.defaultCity))}
                  verifiedLocalities={verifiedLocalities}
                  type={type}
                  restaurantAddress={get(restaurant, 'address', '')}
                  previousSelectedTable={previousSelectedTable}
                  selectedAddress={selectedAddress}
                  handleGetItDeliveredClick={this.handleGetItDeliveredClick}
                  handleLinkClick={this.handleLinkClick}
                />
              )}
            {!isMenuEmpty && (
              <div className="restaurant-container_offers">
                {/* {!isUserLoggedIn() &&
                  get(restaurantOffers, 'hasGuestOffers', false) && ( // check for user's login-in data and then check for hasGuestOffers
                    <ExclusiveOfferCard
                      CTAText={constants.offers.loginCTA}
                      onClick={(event) => this.handleLoginClick(event, authInitiatorFeatures.offer)}
                    />
                  )} */}
                {offers &&
                  offers.map((offer, index) => (
                    <OfferCard
                      orientation="horizontal"
                      key={index}
                      offer={offer}
                      handleViewOfferDetailsClick={(offer) =>
                        this.handleViewOfferDetailsClick(offer)
                      }
                    />
                  ))}
              </div>
            )}
            {!isMenuReadOnly &&
              !isMenuEmpty &&
              (!loadingPreviousOrder ? (
                previousOrderCardsDOMArray.length > 0 && (
                  <div className="restaurant-container_previous-order">
                    <div className="restaurant-container_previous-order_title">
                      {menuScreenConfig.orderAgainTitle}
                    </div>
                    <div className="restaurant-container_previous-order_cards">
                      {previousOrderCardsDOMArray}
                    </div>
                  </div>
                )
              ) : (
                <div className="restaurant-container_previous-order">
                  <div className="restaurant-container_previous-order_title">
                    <Skeleton
                      variant="rectangular"
                      classes={{ rectangular: 'title' }}
                      animation="wave"
                      isLoading={true}
                    />
                  </div>
                  <div className="restaurant-container_previous-order_cards">
                    <Skeleton
                      variant="rectangular"
                      classes={{ rectangular: 'body' }}
                      animation="wave"
                      isLoading={true}
                    />
                  </div>
                </div>
              ))}
            {this.renderItems(
              showRestaurantCashCardFlag
                ? this.renderRestaurantCashCardPreview(restaurantCashCardInfo)
                : null,
              restaurantCashCardInjectablePositionOnMenu,
              isMenuReadOnly,
            )}
            {isMenuEmpty && (
              <div className="restaurant-container_empty-menu">
                <img
                  className="empty-menu-placeholder"
                  src={emptyMenuPlaceholder}
                  alt="empty-menu-placeholder"
                />
                <span className="title">{menuScreenConfig.emptyMenu.placeholderTitle}</span>
                <span className="description">
                  {menuScreenConfig.emptyMenu.placeholderDescription}
                </span>
              </div>
            )}
            {!showEmptySearchMessage && (
              <div className="restaurant-container_footer-branding">
                <Footer />
              </div>
            )}
          </div>
        </div>
        {!isMenuEmpty && showMenuButton && (
          <div
            className={classNames('menu-button', {
              'add-bottom-margin': count > 0 || !isEmpty(restaurantOperatingMode),
            })}
          >
            <img
              className="menubtn-img"
              src={menuBtnImage}
              alt="Menu"
              onClick={this.openMenuModal.bind(this)}
            />
          </div>
        )}
        {showModal && (
          <MenuModal
            menu={itemMenuArr}
            restaurant={restaurant}
            handleItemClick={this.handleItemClick}
            activeItem={activeItem}
            showModal={showModal}
            isMenuCategoryAvailable={this.isMenuCategoryAvailable}
            onClose={this.closeMenuModal}
          />
        )}
        {!isMenuReadOnly && count > 0 && (
          <CartFooter
            restaurant={restaurant}
            itemsCount={count}
            cost={cost}
            onClick={this.onViewCartClick}
            style={{
              position: 'fixed',
              bottom: 0,
              zIndex: visible || isCustomizable ? -1 : 1,
              transform: cost > 0 ? 'translate3d(0,0,0)' : 'translate3d(0,100%,0)',
              transition: '0.2s ease-in',
              height: '4rem',
            }}
          />
        )}
        {isMenuReadOnly && (
          <div className="restaurant-container_cart-footer cart-footer read-only-menu">
            {menuScreenConfig.readOnlyMenu.cartFooterLabel}
          </div>
        )}
        {!isEmpty(restaurantOperatingMode) && (
          <Banner
            position={banner.position}
            color={banner.color}
            message={renderRestaurantOperatingModeMessage(restaurantOperatingMode, type)}
          />
        )}

        {isCustomizable && (
          <CustomizationMenu
            openSegmentPopup={isCustomizable}
            dismissSegmentPopup={this.onCustomizableHide}
            name={get(customizeItem, 'name', '')}
            description={get(customizeItem, 'description', '')}
            price={get(customizeItem, 'price', 0)}
            customizeItem={customizeItem}
            hasVariations={get(customizeItem, 'hasVariations', false)}
            defaultVariation={get(customizeItem, 'defaultVariation', {})}
            onAddToCart={this.onAddToCart}
            onAddNewItem={this.onAddNewItem}
            onRepeatLastItem={({ selectedCustomization, selectedVariation }) =>
              this.onRepeatLastItem({ selectedCustomization, selectedVariation })
            }
            openRepeatCustomizationPopup={openRepeatCustomizationPopup}
            selectedMenuItemId={selectedMenuItemId}
            selectedCustomizationArr={selectedCustomizationArr}
            selectedVariationArr={selectedVariationArr}
            showCustomizationErrorCTA={showCustomizationErrorCTA}
            handleCustomizationErrorCTA={this.handleCustomizationErrorCTA}
            isMenuReadOnly={isMenuReadOnly}
          />
        )}

        {showErrorPopup && (
          <ExceptionComponent
            pageType="HalfSize"
            openSegmentPopup={showErrorPopup}
            dismissSegmentPopup={this.onCloseExceptionComponent}
            title={get(errorContent, 'title')}
            description={get(errorContent, 'description')}
            buttonCta={get(errorContent, 'buttonCta')}
            handleButtonCta={this.handleExceptionCTA}
          />
        )}
        {showMultipleCustomizationPopup && (
          <MultipleCustomizationPopup
            openSegmentPopup={showMultipleCustomizationPopup}
            dismissSegmentPopup={this.onCloseMultipleCustomizationPopup.bind(this)}
            handleCta={this.onViewCartClick}
          />
        )}
        {!isAssistanceUpdated && showServicesComponent && (
          <Services
            openSegmentPopup={showServicesComponent}
            dismissSegmentPopup={this.onCloseServicesComponent.bind(this)}
          />
        )}
        {showSelfPickupPopupComponent && (
          <SelfPickupComponent
            openSegmentPopup={showSelfPickupPopupComponent}
            dismissSegmentPopup={this.onCloseSelfServicePopup.bind(this)}
          />
        )}
        {showDeliveryAddressPopup && (
          <DeliveryAddressPopup
            openSegmentPopup={showDeliveryAddressPopup}
            dismissSegmentPopup={this.onCloseDeliveryAddressPopup}
            loginData={loginData}
            serviceTypes={serviceTypes}
            verifiedLocalities={verifiedLocalities}
            page={page}
            showSelfPickUpSection={showSelfPickUpSection}
            handleDeliveryAddressCTA={({
              addressObj,
              isSelfPickUpSelected,
              isSavedAddressSelected,
            }) =>
              this.handleDeliveryAddressCTA({
                addressObj,
                isSelfPickUpSelected,
                isSavedAddressSelected,
              })
            }
          />
        )}
        {showVerifyCartPopup && (
          <VerifyCartPopup
            openSegmentPopup={showVerifyCartPopup}
            dismissSegmentPopup={this.onCloseVerifyCartPopup}
            handleViewCartClick={this.onViewCartClick}
            handeSwitchRestaurantClick={this.handleSwitchRestaurantClick}
          />
        )}
        {showOrderModePopup && (
          <OrderModePopup
            openSegmentPopup={showOrderModePopup}
            dismissSegmentPopup={this.onCloseOrderModePopup}
            serviceTypes={serviceTypes}
            selectedTable={selectedTable}
            handleServiceModeClick={(service) => this.handleServiceModeClick(service)}
          />
        )}
        {showMenuItemDetailPopup && (
          <MenuItemDetailPopup
            openSegmentPopup={showMenuItemDetailPopup}
            dismissSegmentPopup={this.onCloseMenuItemDetailsPopup}
            mediaItems={mediaItems}
            menuItem={menuItem}
            countMap={countMap}
            onFoodAddClick={this.onFoodAddClick}
            onFoodRemoveClick={this.onFoodRemoveClick}
            isMenuReadOnly={isMenuReadOnly}
          />
        )}
        {showOfferDetailsPopup && (
          <OfferDetailsPopup
            openSegmentPopup={showOfferDetailsPopup}
            dismissSegmentPopup={this.onCloseOfferDetailsPopup.bind(this)}
            offer={offer}
            isOfferApplicable={false}
          />
        )}
        {showPWAInstallPopupFlag && !isRewardAppAvailable && (
          <InstallAppetizerPopup
            openSegmentPopup={showPWAInstallPopupFlag}
            dismissSegmentPopup={this.onClosePWAInstallPopup}
            restaurantEntityName={restaurantEntityName}
            restaurantEntityImage={restaurantEntityImage}
            handleInstallClick={() =>
              isSafari
                ? this.handleSafariPWAInstallClick(whitelabelInfoExists, true)
                : this.handleInstallPWAClick(whitelabelInfoExists)
            }
          />
        )}
        {showSafariPWAInstallPopupFlag && (
          <SafariPWAInstallGuide
            openSegmentPopup={showSafariPWAInstallPopupFlag}
            dismissSegmentPopup={() => this.setShowSafariPWAInstallPopup(false)}
          />
        )}
        {showRestaurantCashCardInfoPopup && (
          <CashCardInfoPopup
            open={showRestaurantCashCardInfoPopup}
            cashCardRestaurants={restaurantCashCardInfo.cashCardRestaurants}
            cashCardName={restaurantCashCardInfo.title}
            cashCardBackgroundColor={restaurantCashCardInfo.colour}
            cashCardMinRechargeAmount={restaurantCashCardInfo.minAmount}
            brandLogo={restaurantCashCardInfo.logo}
            isBackgroundBlendedWithLogo={restaurantCashCardInfo.isBackgroundBlendedWithLogo}
            cashCardUnitPointMonetaryFactor={cashCardUnitPointMonetaryFactor}
            onRechargeClick={this.onRestaurantCashCardRechargeClick}
            isGuestLoggedIn={isGuestLoggedIn}
            initiateLogin={(event) =>
              this.handleLoginClick(event, authInitiatorFeatures.cashCardRecharge)
            }
            onClose={this.handleRestaurantCashCardInfoPopupClose}
          />
        )}
        {showRechargeConfirmationScreen && !isEmpty(rechargeTransactionObject) && (
          <div className="restaurant-container_recharge-screen-wrapper">
            <RechargeScreen
              variant={cashCardScreens.rechargeConfirmation}
              rechargePoints={rechargeTransactionObject.amount}
              cashCardName={guestCashCardInfo.title}
              cashCardBalance={guestCashCardInfo.balance}
              transactionId={rechargeTransactionObject.id}
              transactionType={rechargeTransactionObject.type}
              transactionStatus={rechargeTransactionObject.status}
              transactionDate={rechargeTransactionObject.initiateDate}
              onClose={this.handleRechargeConfirmationScreenClose}
              restaurantEmail={guestCashCardInfo.email}
            />
          </div>
        )}
        {showRechargeOngoingScreen && (
          <div className="restaurant-container_recharge-screen-wrapper">
            <RechargeScreen
              variant={cashCardScreens.rechargeOngoing}
              cashCardName={restaurantCashCardInfo.title}
              cashCardBalance={guestCashCardInfo.balance}
              minRechargeAmount={restaurantCashCardInfo.minAmount}
              onClose={this.handleRechargeOngoingScreenClose}
              history={history}
              location={location}
            />
          </div>
        )}
        {showAuthenticationPopup && (
          <AuthenticationPopup
            openSegmentPopup={showAuthenticationPopup}
            dismissSegmentPopup={this.onCloseAuthenticationPopup}
          />
        )}
        {showGenericPopup && (
          <Popup
            openSegmentPopup={showGenericPopup}
            dismissSegmentPopup={this.onCloseGenericPopupComponent}
            content={genericPopupContent}
            children={genericPopupChildren}
            handleCTA={this.onCloseGenericPopupComponent}
            handleSecondaryCTA={this.handleLoseDealAndProceedCTA}
            variant={'full-screen'}
          />
        )}
        {!isEmpty(selectedPreviousOrder) && (
          <PreviousOrderDetailsPopup
            open={!isEmpty(selectedPreviousOrder)}
            order={selectedPreviousOrder}
            previousOrdersAddedToCart={previousOrdersAddedToCart}
            unavailableOrderItems={this.extractUnavailableMenuItemsForRepeatOrders(
              get(selectedPreviousOrder, 'menuItems', []),
            )}
            onClose={() => this.setState({ selectedPreviousOrder: {} })}
            onAddToCart={this.handlePopupAddPreviousOrderToCart}
            isMenuReadOnly={isMenuReadOnly}
          />
        )}
        {showInstallAppPopup && (
          <InstallAppPopup
            openSegmentPopup={showInstallAppPopup}
            url={getAppLink}
            dismissSegmentPopup={() => this.setShowInstallAppPopup(false)}
          />
        )}
        {
          <PermissionEvent
            startTracking={trackAnalyticEvent}
            vendorType={analyticsVendorType.GTM}
            label={fudrAnalyticsGTMEventLabel.permission.location}
            action={fudrAnalyticsGTMEvents.permission}
            category={fudrAnalyticsGTMEventCategory.permission.user}
            permission={geoLocationPermissionStatus}
            handleStopTracking={this.handleStopTracking}
          />
        }
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  menu: selectMenu(state),
  cart: selectCart(state),
  selectedTable: get(state, 'app.selectedTable', {}),
  restaurant: selectRestaurant(state),
  customization: get(state, 'app.customization'),
  loginData: get(state, 'authentication.loginData', {}),
  searchResult: get(state, 'app.searchResult', []),
  errorResponse: get(state, 'app.errorResponse', {}),
  disabledItems: get(state, 'app.disabledItems', []),
  disabledCategories: get(state, 'app.disabledCategories', []),
  isAssistanceUpdated: get(state, 'app.isAssistanceUpdated', false),
  verifiedLocalities: get(state, 'app.verifiedLocalities', []),
  serviceTypes: get(state, 'app.serviceTypes', []),
  previousSelectedTable: get(state, 'app.previousSelectedTable', {}),
  selectedAddress: get(state, 'app.selectedAddress', {}),
  restaurantGroup: get(state, 'app.restaurantGroup', {}),
  recommendedSections: get(state, 'app.recommendedSections', []),
  restaurantOffers: get(state, 'app.restaurantOffers', {}),
  appStartupInfo: get(state, 'common.appStartupInfo', {}),
  appSettings: get(state, 'app.masterData.appSettings', {}),
  restaurantCashCard: get(state, 'app.restaurantCashCard', []),
  guestCashCards: get(state, 'app.guestCashCards._embedded.guestCashCards', []),
  lastCashCardRechargeTransaction: get(state, 'app.lastCashCardRechargeTransaction', {}),
  viewCartItems: get(state, 'app.viewCart', {}),
  restaurantConfigAdditionalProps: get(
    state,
    'app.restaurant.restaurantConfig.additionalProperties',
    [],
  ),
  progressiveWebApp: get(state, 'app.progressiveWebApp', {}),
  previousOrders: get(state, 'app.previousOrders', []),
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      findGuestLocationAction,
      findGeoLocationAction,
      fetchRestaurantMenuAction,
      fetchRestaurantOffersAction,
      fetchMenuItemCustomizationsAction,
      clearPreviousCustomization,
      fetchViewCartItemsAction,
      updateMenuAction,
      clearViewCartItems,
      clearApiErrorResponseAction,
      clearDisabledMenuItems,
      updateAssistanceStatusAction,
      dispatchPreviousSelectedTable: dispatchPreviousSelectedTableAction,
      fetchSelectedTable: fetchSelectedTableAction,
      dispatchSelectedAddress: dispatchSelectedAddressAction,
      updateProfileAction,
      dispatchParentRouteAction,
      fetchRestaurantCashCard: fetchRestaurantCashCardAction,
      clearRestaurantCashCard: clearRestaurantCashCardAction,
      fetchGuestCashCards: fetchGuestCashCardsAction,
      signInByTrueCallerAction,
      dispatchGuestLoginDataAction,
      beginAjaxCallAction,
      endAjaxCallAction,
      dispatchLastCashCardRechargeTransaction: dispatchLastCashCardRechargeTransactionAction,
      fetchGuestCashCardDetails: fetchGuestCashCardDetailsAction,
      dispatchUserCart: dispatchUserCartAction,
      clearProgressiveWebAppData: clearProgressiveWebAppDataAction,
      fetchGuestPreviousOrder: fetchGuestPreviousOrdersAction,
      clearGuestPreviousOrders: clearGuestPreviousOrdersAction,
      fetchAppStartupInfo: fetchAppStartupInfoAction,
      dispatchProgressiveWebAppData: dispatchProgressiveWebAppDataAction,
      fetchMenuItemVariations: fetchMenuItemVariationsAction,
    },
    dispatch,
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(Restaurant);
